import React, { useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import FormField from '../ui/FormField';
import SectionCard from '../ui/SectionCard';
import FeatureGate from '../payment/FeatureGate';
import RentCalculator from './RentCalculator';
import SalesProjectionsCalculator from './SalesProjectionsCalculator';
import ManagementCostProjector from './ManagementCostProjector';
import DocumentGenerator from '../documents/DocumentGenerator';
import AIAssistant from '../ai/AIAssistant';
import PLImporter from './PLImporter';
import ExcelFinancialImporter from './ExcelFinancialImporter';
import ScenarioManager from './ScenarioManager';
import MonthlyStatement from './MonthlyStatement';
import { Calculator, TrendingUp, DollarSign, AlertTriangle, BarChart3, Download, CheckCircle, Building2, Store, Wrench, Sparkles, FileText, Plus, Trash2 } from 'lucide-react';
import { OPERATING_EXPENSE_KEYS, OPERATING_EXPENSE_LABELS, STARTUP_COST_KEYS, STARTUP_COST_LABELS } from '../../config/pnlLineItems';
import { MONTHLY_PL_SECTIONS, getInitialMonthlyPL } from '../../config/monthlyPLAccounts';
import { LOCATION_OPTIONS } from '../../config/locationOptions';

const FinancialProjections = () => {
  const { state, actions } = useApp();
  const data = state.financialData || {};

  // Get restaurant type data or initialize
  const restaurantType = data.restaurantType || { type: 'new', buildCosts: 0, purchasePrice: 0, renovations: 0, needsRenovations: false };
  
  const handleRestaurantTypeChange = (field, value) => {
    actions.updateFinancialData('restaurantType', {
      ...restaurantType,
      [field]: value
    });
  };

  // Monthly P&L: merge saved data with defaults so all account codes exist
  const monthlyPL = useMemo(() => {
    const defaults = getInitialMonthlyPL();
    const saved = data.monthlyPL || {};
    const merged = {};
    MONTHLY_PL_SECTIONS.forEach(({ id }) => {
      merged[id] = { ...(defaults[id] || {}), ...(saved[id] || {}) };
    });
    return merged;
  }, [data.monthlyPL]);

  const handleMonthlyPLChange = (sectionId, code, value) => {
    const num = value === '' ? 0 : parseFloat(value) || 0;
    const sectionData = monthlyPL[sectionId] || {};
    actions.updateFinancialData('monthlyPL', {
      ...monthlyPL,
      [sectionId]: { ...sectionData, [code]: num }
    });
  };

  const handleFieldChange = (section, field, value) => {
    // Check if this is a time field (contains 'open' or 'close' in the path)
    const isTimeField = field.includes('open') || field.includes('close');
    // Check if this is a boolean field (contains 'closed')
    const isBooleanField = field.includes('closed');
    
    // Determine the value type
    let processedValue;
    if (typeof value === 'object' && value !== null) {
      // If value is an object, use it directly (for updating entire day object)
      processedValue = value;
    } else if (isBooleanField) {
      processedValue = value; // Boolean value
    } else if (isTimeField) {
      processedValue = value; // String value for time (e.g., "11:00")
    } else {
      // Numeric value
      processedValue = value === '' ? 0 : parseFloat(value) || 0;
    }
    
    // Handle nested fields (e.g., 'averageCheck.lunch', 'hoursOfOperation.monday.open')
    if (field.includes('.')) {
      const parts = field.split('.');
      const currentData = data[section] || {};
      
      // Deep clone to avoid mutation
      const updatedData = JSON.parse(JSON.stringify(currentData));
      
      // Special handling for hoursOfOperation when updating entire day object
      if (field.startsWith('hoursOfOperation.') && typeof processedValue === 'object') {
        // Extract day from field path (e.g., "hoursOfOperation.monday" -> "monday")
        const dayIndex = parts.findIndex(p => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(p));
        if (dayIndex >= 0) {
          const day = parts[dayIndex];
          // Ensure hoursOfOperation exists
          if (!updatedData.hoursOfOperation) {
            updatedData.hoursOfOperation = {};
          }
          // Update the entire day object
          updatedData.hoursOfOperation[day] = processedValue;
        } else {
          // Fallback to standard nested update
          let current = updatedData;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
              current[parts[i]] = {};
            }
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = processedValue;
        }
      } else {
        // Navigate to nested property and set value
        let current = updatedData;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        // Special handling for hoursOfOperation to preserve all day properties
        if (parts[0] === 'hoursOfOperation' && parts.length === 3) {
          const day = parts[1];
          const property = parts[2];
          // Ensure the day object exists with all properties
          if (!updatedData.hoursOfOperation) {
            updatedData.hoursOfOperation = {};
          }
          if (!updatedData.hoursOfOperation[day]) {
            updatedData.hoursOfOperation[day] = { closed: false, open: '11:00', close: '22:00' };
          }
          // Update the specific property while preserving others
          updatedData.hoursOfOperation[day][property] = processedValue;
        } else {
          // Set the final value
          current[parts[parts.length - 1]] = processedValue;
        }
      }
      
      // Update the entire section
      actions.updateFinancialData(section, updatedData);
    } else {
      // Simple field update
      const currentData = data[section] || {};
      actions.updateFinancialData(section, { ...currentData, [field]: processedValue });
    }
  };

  const expenseVendors = data.expenseVendors || { operating: {}, startup: {} };
  const setExpenseVendor = (type, key, vendorId) => {
    const current = expenseVendors[type] || {};
    actions.updateFinancialData('expenseVendors', {
      ...expenseVendors,
      [type]: { ...current, [key]: vendorId || null }
    });
  };

  // Boston Restaurant Industry Benchmarks
  const bostonBenchmarks = {
    avgRentPerSqFt: 45, // $45/sq ft annually in Boston
    avgUtilitiesPerSqFt: 8, // $8/sq ft annually
    avgSeatPrice: 40, // Average check per person
    avgTurnovers: 2.5, // Table turnovers per day
    avgFoodCostPercent: 0.28, // 28% food costs
    avgLaborPercent: 0.32, // 32% labor costs
    avgRevPerSeat: 75000, // Annual revenue per seat
    avgStartupCostPerSeat: 1500, // Average startup cost per seat
  };

  // Calculate daily sales projections
  const dailySalesProjections = useMemo(() => {
    // Default hours of operation (all days open by default)
    const defaultHours = {
      monday: { open: '11:00', close: '22:00', closed: false },
      tuesday: { open: '11:00', close: '22:00', closed: false },
      wednesday: { open: '11:00', close: '22:00', closed: false },
      thursday: { open: '11:00', close: '22:00', closed: false },
      friday: { open: '11:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '21:00', closed: false }
    };
    
    const ops = data.restaurantOperations || {
      seats: 50,
      hoursOfOperation: defaultHours,
      averageCheck: { lunch: 18, dinner: 32, brunch: 24, beverages: 8 },
      tableTurnover: { lunch: 1.5, dinner: 2.0, brunch: 2.5, average: 2.0 },
      occupancyRate: { lunch: 0.7, dinner: 0.9, brunch: 0.8, average: 0.8 },
      serviceTypes: { lunch: true, dinner: true, brunch: false }
    };
    
    // Get enabled service types (default to lunch and dinner enabled, brunch disabled)
    const serviceTypes = ops.serviceTypes || { lunch: true, dinner: true, brunch: false };
    const lunchEnabled = serviceTypes.lunch !== false;
    const dinnerEnabled = serviceTypes.dinner !== false;
    const brunchEnabled = serviceTypes.brunch === true;
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    const dailyProjections = days.map(day => {
      const hours = ops.hoursOfOperation?.[day] || defaultHours[day] || { closed: false, open: '11:00', close: '22:00' };
      if (hours.closed) return { day, totalRevenue: 0, covers: 0, foodSales: 0, beverageSales: 0, lunchCovers: 0, dinnerCovers: 0, brunchCovers: 0, lunchRevenue: 0, dinnerRevenue: 0, brunchRevenue: 0, beverageRevenue: 0 };
      
      // Parse hours
      const openTime = new Date(`2000-01-01 ${hours.open}`);
      const closeTime = new Date(`2000-01-01 ${hours.close}`);
      let totalHours = (closeTime - openTime) / (1000 * 60 * 60);
      
      // Handle negative hours (e.g., if close time is earlier than open time)
      if (totalHours < 0) totalHours = 0;
      
      // Determine meal periods based on enabled services
      let lunchHours = 0;
      let dinnerHours = 0;
      let brunchHours = 0;
      
      if (brunchEnabled && (day === 'saturday' || day === 'sunday')) {
        brunchHours = Math.min(totalHours, 3); // Brunch takes up to 3 hours (10am-1pm typically)
        const remainingHours = Math.max(0, totalHours - brunchHours);
        if (lunchEnabled && dinnerEnabled) {
          // Split remaining hours between lunch and dinner
          lunchHours = Math.min(remainingHours, 4);
          dinnerHours = Math.max(0, remainingHours - lunchHours);
        } else if (lunchEnabled) {
          lunchHours = remainingHours;
        } else if (dinnerEnabled) {
          dinnerHours = remainingHours;
        }
      } else {
        // No brunch - split between lunch and dinner
        if (lunchEnabled && dinnerEnabled) {
          lunchHours = Math.min(totalHours, 4); // Lunch gets first 4 hours
          dinnerHours = Math.max(0, totalHours - lunchHours);
        } else if (lunchEnabled) {
          lunchHours = totalHours;
        } else if (dinnerEnabled) {
          dinnerHours = totalHours;
        }
      }
      
      // Calculate covers per meal period (only for enabled services)
      // Formula: Seats × Occupancy Rate × Table Turnover (per hour) × Hours = Total Covers
      const lunchCovers = lunchEnabled ? Math.floor(ops.seats * ops.occupancyRate.lunch * ops.tableTurnover.lunch * lunchHours) : 0;
      const dinnerCovers = dinnerEnabled ? Math.floor(ops.seats * ops.occupancyRate.dinner * ops.tableTurnover.dinner * dinnerHours) : 0;
      const brunchCovers = brunchEnabled ? Math.floor(ops.seats * ops.occupancyRate.brunch * ops.tableTurnover.brunch * brunchHours) : 0;
      
      const totalCovers = lunchCovers + dinnerCovers + brunchCovers;
      
      // Calculate revenue by meal period (only for enabled services)
      const lunchRevenue = lunchEnabled ? lunchCovers * (ops.averageCheck.lunch || 0) : 0;
      const dinnerRevenue = dinnerEnabled ? dinnerCovers * (ops.averageCheck.dinner || 0) : 0;
      const brunchRevenue = brunchEnabled ? brunchCovers * (ops.averageCheck.brunch || 0) : 0;
      const beverageRevenue = totalCovers * (ops.averageCheck.beverages || 0) * 0.7; // 70% order beverages
      
      const totalRevenue = lunchRevenue + dinnerRevenue + brunchRevenue + beverageRevenue;
      const foodSales = lunchRevenue + dinnerRevenue + brunchRevenue;
      const beverageSales = beverageRevenue;
      
      return {
        day,
        totalRevenue,
        covers: totalCovers,
        foodSales,
        beverageSales,
        lunchCovers,
        dinnerCovers,
        brunchCovers,
        lunchRevenue,
        dinnerRevenue,
        brunchRevenue,
        beverageRevenue
      };
    });
    
    const weeklyTotal = dailyProjections.reduce((sum, day) => sum + day.totalRevenue, 0);
    const weeklyCovers = dailyProjections.reduce((sum, day) => sum + day.covers, 0);
    
    return {
      daily: dailyProjections,
      weekly: {
        totalRevenue: weeklyTotal,
        covers: weeklyCovers,
        foodSales: dailyProjections.reduce((sum, day) => sum + day.foodSales, 0),
        beverageSales: dailyProjections.reduce((sum, day) => sum + day.beverageSales, 0)
      },
      annual: {
        totalRevenue: weeklyTotal * 52,
        covers: weeklyCovers * 52,
        foodSales: dailyProjections.reduce((sum, day) => sum + day.foodSales, 0) * 52,
        beverageSales: dailyProjections.reduce((sum, day) => sum + day.beverageSales, 0) * 52
      }
    };
  }, [data.restaurantOperations]);

  // Calculate detailed labor projections
  const laborProjections = useMemo(() => {
    const labor = data.operatingExpenses?.labor || {
      management: [],
      frontOfHouse: [],
      backOfHouse: [],
      support: [],
      taxRates: {
        federalIncomeTax: 0.22,
        stateIncomeTax: 0.05,
        socialSecurity: 0.062,
        medicare: 0.0145,
        unemployment: 0.006,
        workersComp: 0.015,
        benefitsRate: 0.30
      }
    };
    const taxRates = labor.taxRates || {
      federalIncomeTax: 0.22,
      stateIncomeTax: 0.05,
      socialSecurity: 0.062,
      medicare: 0.0145,
      unemployment: 0.006,
      workersComp: 0.015,
      benefitsRate: 0.30
    };
    
    const calculateEmployeeCosts = (employee) => {
      const weeklyHours = employee.hoursPerWeek;
      const weeklyBasePay = weeklyHours * employee.hourlyRate;
      const weeklyTips = weeklyHours * employee.tips;
      const weeklyGrossPay = weeklyBasePay + weeklyTips;
      
      // Calculate taxes
      const federalTax = weeklyGrossPay * taxRates.federalIncomeTax;
      const stateTax = weeklyGrossPay * taxRates.stateIncomeTax;
      const socialSecurity = weeklyGrossPay * taxRates.socialSecurity;
      const medicare = weeklyGrossPay * taxRates.medicare;
      const unemployment = weeklyGrossPay * taxRates.unemployment;
      const workersComp = weeklyGrossPay * taxRates.workersComp;
      
      // Calculate employer costs
      const employerSocialSecurity = weeklyBasePay * taxRates.socialSecurity;
      const employerMedicare = weeklyBasePay * taxRates.medicare;
      const employerUnemployment = weeklyBasePay * taxRates.unemployment;
      const employerWorkersComp = weeklyBasePay * taxRates.workersComp;
      
      // Benefits (only for eligible positions)
      const benefitsCost = employee.benefits ? weeklyBasePay * taxRates.benefitsRate : 0;
      
      const totalEmployeeCost = weeklyGrossPay + employerSocialSecurity + employerMedicare + 
                               employerUnemployment + employerWorkersComp + benefitsCost;
      
      return {
        ...employee,
        weeklyBasePay,
        weeklyTips,
        weeklyGrossPay,
        federalTax,
        stateTax,
        socialSecurity,
        medicare,
        unemployment,
        workersComp,
        employerSocialSecurity,
        employerMedicare,
        employerUnemployment,
        employerWorkersComp,
        benefitsCost,
        totalEmployeeCost,
        netPay: weeklyGrossPay - federalTax - stateTax - socialSecurity - medicare,
        annualCost: totalEmployeeCost * 52
      };
    };

    // Process all position arrays
    const management = (labor.management || []).map(calculateEmployeeCosts);
    const frontOfHouse = (labor.frontOfHouse || []).map(calculateEmployeeCosts);
    const backOfHouse = (labor.backOfHouse || []).map(calculateEmployeeCosts);
    const support = (labor.support || []).map(calculateEmployeeCosts);

    const allPositions = [...management, ...frontOfHouse, ...backOfHouse, ...support];

    const weeklyTotals = {
      basePay: allPositions.reduce((sum, pos) => sum + pos.weeklyBasePay, 0),
      tips: allPositions.reduce((sum, pos) => sum + pos.weeklyTips, 0),
      grossPay: allPositions.reduce((sum, pos) => sum + pos.weeklyGrossPay, 0),
      employeeTaxes: allPositions.reduce((sum, pos) => sum + pos.federalTax + pos.stateTax + pos.socialSecurity + pos.medicare, 0),
      employerTaxes: allPositions.reduce((sum, pos) => sum + pos.employerSocialSecurity + pos.employerMedicare + pos.employerUnemployment + pos.employerWorkersComp, 0),
      benefits: allPositions.reduce((sum, pos) => sum + pos.benefitsCost, 0),
      totalCost: allPositions.reduce((sum, pos) => sum + pos.totalEmployeeCost, 0)
    };

    const annualTotals = {
      basePay: weeklyTotals.basePay * 52,
      tips: weeklyTotals.tips * 52,
      grossPay: weeklyTotals.grossPay * 52,
      employeeTaxes: weeklyTotals.employeeTaxes * 52,
      employerTaxes: weeklyTotals.employerTaxes * 52,
      benefits: weeklyTotals.benefits * 52,
      totalCost: weeklyTotals.totalCost * 52
    };

    return {
      positions: {
        management,
        frontOfHouse,
        backOfHouse,
        support
      },
      weekly: weeklyTotals,
      annual: annualTotals,
      allPositions
    };
  }, [data.operatingExpenses?.labor]);

  // Calculate comprehensive financial analysis
  const financialAnalysis = useMemo(() => {
    const marketTrends = data.marketTrends || {};
    const yearlyPermits = data.yearlyPermits || {
      businessPermits: {},
      insuranceCosts: {},
      professionalServices: {}
    };
    const maintenanceCosts = data.maintenanceCosts || {
      equipmentMaintenance: {},
      buildingMaintenance: {},
      equipmentReplacement: []
    };
    
    // Calculate total yearly permits and compliance costs
    const totalBusinessPermits = yearlyPermits.businessPermits 
      ? Object.values(yearlyPermits.businessPermits).reduce((sum, cost) => sum + (cost || 0), 0)
      : 0;
    const totalInsuranceCosts = yearlyPermits.insuranceCosts
      ? Object.values(yearlyPermits.insuranceCosts).reduce((sum, cost) => sum + (cost || 0), 0)
      : 0;
    const totalProfessionalServices = yearlyPermits.professionalServices
      ? Object.values(yearlyPermits.professionalServices).reduce((sum, cost) => sum + (cost || 0), 0)
      : 0;
    const totalYearlyPermits = totalBusinessPermits + totalInsuranceCosts + totalProfessionalServices;
    
    // Calculate total maintenance costs
    const totalEquipmentMaintenance = maintenanceCosts.equipmentMaintenance
      ? Object.values(maintenanceCosts.equipmentMaintenance).reduce((sum, cost) => sum + (cost || 0), 0)
      : 0;
    const totalBuildingMaintenance = maintenanceCosts.buildingMaintenance
      ? Object.values(maintenanceCosts.buildingMaintenance).reduce((sum, cost) => sum + (cost || 0), 0)
      : 0;
    const totalEquipmentReplacement = maintenanceCosts.equipmentReplacement && Array.isArray(maintenanceCosts.equipmentReplacement)
      ? maintenanceCosts.equipmentReplacement.reduce((sum, item) => sum + (item?.annualReserve || 0), 0)
      : 0;
    const totalMaintenanceCosts = totalEquipmentMaintenance + totalBuildingMaintenance + totalEquipmentReplacement;
    
    // Calculate 5-year projections
    const projections = [];
    for (let year = 1; year <= 5; year++) {
      const growthData = marketTrends.growthProjections?.[`year${year}`] || { revenueGrowth: 1, costInflation: 1 };
      const baseRevenue = dailySalesProjections.annual.totalRevenue;
      const projectedRevenue = baseRevenue * growthData.revenueGrowth;
      const projectedLaborCost = laborProjections.annual.totalCost * growthData.costInflation;
      const projectedPermitsCost = totalYearlyPermits * growthData.costInflation;
      const projectedMaintenanceCost = totalMaintenanceCosts * growthData.costInflation;
      
      // Calculate seasonal revenue adjustments
      const seasonalRevenue = projectedRevenue * 1.05; // Average seasonal factor
      
      projections.push({
        year,
        revenue: projectedRevenue,
        seasonalRevenue,
        laborCost: projectedLaborCost,
        permitsCost: projectedPermitsCost,
        maintenanceCost: projectedMaintenanceCost,
        totalCosts: projectedLaborCost + projectedPermitsCost + projectedMaintenanceCost,
        netIncome: projectedRevenue - (projectedLaborCost + projectedPermitsCost + projectedMaintenanceCost),
        growthRate: (growthData.revenueGrowth - 1) * 100,
        costInflation: (growthData.costInflation - 1) * 100
      });
    }
    
    // Calculate industry benchmarks comparison
    const seats = data.restaurantOperations?.seats || 50;
    const avgRevenuePerSeat = marketTrends?.industryBenchmarks?.avgRevenuePerSeat || bostonBenchmarks.avgRevPerSeat;
    const industryRevenueTarget = avgRevenuePerSeat * seats;
    const currentRevenue = dailySalesProjections.annual.totalRevenue;
    const revenueVsBenchmark = industryRevenueTarget > 0 ? (currentRevenue / industryRevenueTarget) * 100 : 0;
    
    // Calculate seasonal projections
    const seasonalProjections = marketTrends?.seasonalFactors 
      ? Object.entries(marketTrends.seasonalFactors).map(([season, seasonData]) => ({
          season,
          factor: seasonData?.factor || 1,
          months: seasonData?.months || [],
          monthlyRevenue: (dailySalesProjections.annual.totalRevenue / 12) * (seasonData?.factor || 1),
          quarterlyRevenue: (dailySalesProjections.annual.totalRevenue / 4) * (seasonData?.factor || 1)
        }))
      : [];
    
    return {
      totalYearlyPermits,
      totalMaintenanceCosts,
      projections,
      industryBenchmarks: {
        revenueTarget: industryRevenueTarget,
        currentRevenue,
        performance: revenueVsBenchmark
      },
      seasonalProjections,
      breakdown: {
        businessPermits: totalBusinessPermits,
        insuranceCosts: totalInsuranceCosts,
        professionalServices: totalProfessionalServices,
        equipmentMaintenance: totalEquipmentMaintenance,
        buildingMaintenance: totalBuildingMaintenance,
        equipmentReplacement: totalEquipmentReplacement
      }
    };
  }, [data.marketTrends, data.yearlyPermits, data.maintenanceCosts, dailySalesProjections.annual.totalRevenue, laborProjections.annual.totalCost]);

  // Calculate funding sources and debt service
  const fundingAnalysis = useMemo(() => {
    const fundingSources = data.fundingSources || {};
    const fundingTerms = fundingSources?.fundingTerms || {
      interestRates: {
        bankLoans: 7,
        sbaLoans: 6,
        equipmentFinancing: 8,
        familyLoans: 3,
        friendLoans: 4,
        lineOfCredit: 5,
        businessCreditCards: 18
      },
      repaymentTerms: {
        bankLoans: 60,
        sbaLoans: 120,
        equipmentFinancing: 60,
        familyLoans: 60,
        friendLoans: 36
      }
    };
    
    // Calculate total funding
    const personalFunding = (fundingSources.personalSavings || 0) + (fundingSources.personalAssets || 0) + 
                           (fundingSources.homeEquity || 0) + (fundingSources.retirementFunds || 0);
    
    const familyFriendsFunding = (fundingSources.familyLoans || 0) + (fundingSources.friendLoans || 0) + 
                                (fundingSources.familyGifts || 0);
    
    const traditionalFinancing = (fundingSources.bankLoans || 0) + (fundingSources.sbaLoans || 0) + 
                                (fundingSources.equipmentFinancing || 0) + (fundingSources.lineOfCredit || 0) + 
                                (fundingSources.businessCreditCards || 0);
    
    const alternativeFinancing = (fundingSources.crowdfunding || 0) + (fundingSources.angelInvestors || 0) + 
                                (fundingSources.ventureCapital || 0) + (fundingSources.grants || 0) + 
                                (fundingSources.privateInvestors || 0);
    
    const otherFunding = (fundingSources.businessPartners || 0) + (fundingSources.supplierCredit || 0) + 
                        (fundingSources.otherSources || 0);
    
    const totalFunding = personalFunding + familyFriendsFunding + traditionalFinancing + 
                        alternativeFinancing + otherFunding;
    
    // Calculate debt service (monthly payments)
    const interestRates = fundingTerms?.interestRates || {};
    const repaymentTerms = fundingTerms?.repaymentTerms || {};
    
    const debtService = {
      bankLoans: (fundingSources.bankLoans || 0) > 0 && interestRates.bankLoans && repaymentTerms.bankLoans ? 
        ((fundingSources.bankLoans * (interestRates.bankLoans / 100 / 12)) / 
        (1 - Math.pow(1 + (interestRates.bankLoans / 100 / 12), -repaymentTerms.bankLoans))) : 0,
      
      sbaLoans: (fundingSources.sbaLoans || 0) > 0 && interestRates.sbaLoans && repaymentTerms.sbaLoans ? 
        ((fundingSources.sbaLoans * (interestRates.sbaLoans / 100 / 12)) / 
        (1 - Math.pow(1 + (interestRates.sbaLoans / 100 / 12), -repaymentTerms.sbaLoans))) : 0,
      
      equipmentFinancing: (fundingSources.equipmentFinancing || 0) > 0 && interestRates.equipmentFinancing && repaymentTerms.equipmentFinancing ? 
        ((fundingSources.equipmentFinancing * (interestRates.equipmentFinancing / 100 / 12)) / 
        (1 - Math.pow(1 + (interestRates.equipmentFinancing / 100 / 12), -repaymentTerms.equipmentFinancing))) : 0,
      
      familyLoans: (fundingSources.familyLoans || 0) > 0 && interestRates.familyLoans && repaymentTerms.familyLoans ? 
        ((fundingSources.familyLoans * (interestRates.familyLoans / 100 / 12)) / 
        (1 - Math.pow(1 + (interestRates.familyLoans / 100 / 12), -repaymentTerms.familyLoans))) : 0,
      
      friendLoans: (fundingSources.friendLoans || 0) > 0 && interestRates.friendLoans && repaymentTerms.friendLoans ? 
        ((fundingSources.friendLoans * (interestRates.friendLoans / 100 / 12)) / 
        (1 - Math.pow(1 + (interestRates.friendLoans / 100 / 12), -repaymentTerms.friendLoans))) : 0,
      
      lineOfCredit: (fundingSources.lineOfCredit || 0) * ((interestRates.lineOfCredit || 5) / 100 / 12),
      businessCreditCards: (fundingSources.businessCreditCards || 0) * ((interestRates.businessCreditCards || 18) / 100 / 12)
    };
    
    const totalMonthlyDebtService = Object.values(debtService).reduce((sum, payment) => sum + payment, 0);
    const totalAnnualDebtService = totalMonthlyDebtService * 12;
    
    // Calculate funding gap (use same logic as calculations useMemo for consistency)
    const startupCosts = data.startupCosts || {};
    const restaurantType = data.restaurantType || {};
    const totalStartupCosts = 
      (restaurantType.type === 'new' ? (startupCosts.totalBuildCost || 0) : 0) +
      (restaurantType.type === 'existing' ? (startupCosts.purchasePrice || 0) : 0) +
      (restaurantType.type === 'existing' && restaurantType.needsRenovations ? (startupCosts.renovationCosts || 0) : 0) +
      (startupCosts.leaseholdImprovements || 0) +
      (startupCosts.kitchenEquipment || 0) +
      (startupCosts.furnitureFixtures || 0) +
      (startupCosts.initialInventory || 0) +
      (startupCosts.preOpeningSalaries || 0) +
      (startupCosts.depositsLicenses || 0) +
      (startupCosts.initialMarketing || 0) +
      (startupCosts.contingency || 0) +
      (startupCosts.signage || 0) +
      (startupCosts.posHardware || 0) +
      (startupCosts.smallwaresStartup || 0) +
      (startupCosts.openingBeverageInventory || 0) +
      (startupCosts.trainingPreOpen || 0) +
      (startupCosts.securityInstall || 0) +
      (startupCosts.hoodVentilation || 0) +
      (startupCosts.plumbingElectrical || 0) +
      (startupCosts.professionalFees || 0) +
      (startupCosts.preOpeningRent || 0) +
      (startupCosts.otherStartup || 0) +
      (startupCosts.realEstateLand || 0) +
      (startupCosts.realEstateBuildings || 0) +
      (startupCosts.equipment || 0) +
      (startupCosts.vehicles || 0) +
      (startupCosts.otherFixedAssets || 0) +
      (startupCosts.prepaidInsurance || 0) +
      (startupCosts.rentDeposits || 0) +
      (startupCosts.utilityDeposits || 0) +
      (startupCosts.supplies || 0) +
      (startupCosts.workingCapitalCashOnHand || 0);
    const fundingGap = totalStartupCosts - totalFunding;
    
    return {
      totalFunding,
      totalStartupCosts,
      fundingGap,
      totalMonthlyDebtService,
      totalAnnualDebtService,
      breakdown: {
        personalFunding,
        familyFriendsFunding,
        traditionalFinancing,
        alternativeFinancing,
        otherFunding
      },
      debtService
    };
  }, [data.fundingSources, data.startupCosts, data.restaurantType]);

  // Pre-opening budget: costs that occur before opening (runway view)
  const preOpeningBudget = useMemo(() => {
    const startup = data.startupCosts || {};
    const total =
      (startup.preOpeningSalaries || 0) +
      (startup.preOpeningRent || 0) +
      (startup.trainingPreOpen || 0) +
      (startup.professionalFees || 0) +
      (startup.initialMarketing || 0) +
      (startup.depositsLicenses || 0);
    return {
      total,
      breakdown: {
        preOpeningSalaries: startup.preOpeningSalaries || 0,
        preOpeningRent: startup.preOpeningRent || 0,
        trainingPreOpen: startup.trainingPreOpen || 0,
        professionalFees: startup.professionalFees || 0,
        initialMarketing: startup.initialMarketing || 0,
        depositsLicenses: startup.depositsLicenses || 0
      }
    };
  }, [data.startupCosts]);

  // Calculate key metrics
  const calculations = useMemo(() => {
    // CRITICAL FIX: Use dailySalesProjections instead of data.revenue
    // dailySalesProjections is calculated from actual operations data
    const revenue = dailySalesProjections.annual;
    const totalRevenue = revenue.totalRevenue || 0;
    const totalFoodSales = revenue.foodSales || 0;
    const totalBeverageSales = revenue.beverageSales || 0;
    
    // Add other revenue sources from data.revenue if they exist
    const otherRevenue = (data.revenue?.merchandiseSales || 0) + 
                        (data.revenue?.cateringSales || 0) + 
                        (data.revenue?.otherRevenue || 0);
    const totalRevenueWithOther = totalRevenue + otherRevenue;

    // Calculate COGS - use percentages from data.cogs
    const cogs = data.cogs || {};
    const foodCogs = totalFoodSales * (cogs.foodCogsPercent || 0.28);
    const beverageCogs = totalBeverageSales * (cogs.beverageCogsPercent || 0.22);
    const otherCogs = otherRevenue * (cogs.otherCogsPercent || 0.10);
    const totalCogs = foodCogs + beverageCogs + otherCogs;

    const grossProfit = totalRevenueWithOther - totalCogs;
    const grossMargin = totalRevenueWithOther > 0 ? (grossProfit / totalRevenueWithOther) * 100 : 0;

    // Calculate operating expenses with null safety
    const opEx = data.operatingExpenses || {};
    const totalOpEx = 
      (opEx.rent || 0) +
      (opEx.utilities || 0) +
      (opEx.insurance || 0) +
      (opEx.marketing || 0) +
      (opEx.legalAccounting || 0) +
      (opEx.repairsMaintenance || 0) +
      (opEx.supplies || 0) +
      (opEx.adminOffice || 0) +
      (opEx.otherOperatingExpenses || 0) +
      // Detailed operational expenses (with null checks)
      (opEx.linenService || 0) +
      (opEx.trashCollection || 0) +
      (opEx.compostService || 0) +
      (opEx.cleaningService || 0) +
      (opEx.hoodCleaning || 0) +
      (opEx.greaseTrapCleaning || 0) +
      (opEx.internetPhone || 0) +
      (opEx.securitySystem || 0) +
      (opEx.pestControl || 0) +
      (opEx.wasteManagement || 0) +
      (opEx.equipmentMaintenance || 0) +
      (opEx.uniformService || 0) +
      (opEx.dishwareReplacement || 0) +
      (opEx.cleaningSupplies || 0) +
      (opEx.paperGoods || 0) +
      (opEx.kitchenSupplies || 0) +
      (opEx.pointOfSale || 0) +
      (opEx.creditCardProcessing || 0) +
      (opEx.bankFees || 0) +
      (opEx.permitsLicenses || 0) +
      (opEx.musicLicensing || 0) +
      (opEx.deliveryServiceFees || 0) +
      (opEx.trainingCertification || 0) +
      (opEx.employeeBenefits || 0) +
      (opEx.workersCompensation || 0) +
      (opEx.unemploymentInsurance || 0) +
      (opEx.healthInsurance || 0) +
      (opEx.retirementBenefits || 0) +
      (opEx.uniformLaundry || 0) +
      (opEx.parkingFees || 0) +
      (opEx.storageFees || 0) +
      (opEx.professionalServices || 0) +
      (opEx.technologySupport || 0) +
      (opEx.softwareSubscriptions || 0) +
      (opEx.inventoryManagement || 0) +
      (opEx.qualityControl || 0) +
      (opEx.safetyTraining || 0) +
      (opEx.equipmentRental || 0) +
      (opEx.temporaryStaffing || 0) +
      (opEx.consulting || 0) +
      (opEx.travelEntertainment || 0) +
      (opEx.charitableDonations || 0) +
      (opEx.localTaxes || 0) +
      (opEx.propertyTaxes || 0) +
      (opEx.businessTaxes || 0) +
      (opEx.depreciation || 0) +
      (opEx.amortization || 0) +
      (opEx.interestExpense || 0) +
      (opEx.loanFees || 0) +
      (opEx.vehicleExpenses || 0) +
      (opEx.menuPrinting || 0) +
      (opEx.websiteMaintenance || 0) +
      (opEx.emailMarketing || 0) +
      (opEx.socialMediaManagement || 0) +
      (opEx.photography || 0) +
      (opEx.reservationSystem || 0) +
      (opEx.giftCardFees || 0) +
      (opEx.thirdPartyDeliveryFees || 0) +
      (opEx.employeeMeals || 0) +
      (opEx.smallwares || 0) +
      (opEx.eventHosting || 0) +
      (opEx.salaryOwners || 0);

    const totalPayroll = laborProjections.annual.totalCost || 0;
    
    // Add yearly permits and maintenance costs
    const totalYearlyPermits = financialAnalysis.totalYearlyPermits || 0;
    const totalMaintenanceCosts = financialAnalysis.totalMaintenanceCosts || 0;
    const totalDebtService = fundingAnalysis.totalAnnualDebtService || 0;

    const totalExpenses = totalOpEx + totalPayroll + totalYearlyPermits + totalMaintenanceCosts + totalDebtService;
    const netIncome = grossProfit - totalExpenses;
    const netMargin = totalRevenueWithOther > 0 ? (netIncome / totalRevenueWithOther) * 100 : 0;

    // Calculate startup costs - include all relevant fields
    const startupCosts = data.startupCosts || {};
    const restaurantType = data.restaurantType || {};
    const totalStartupCosts = 
      (restaurantType.type === 'new' ? (startupCosts.totalBuildCost || 0) : 0) +
      (restaurantType.type === 'existing' ? (startupCosts.purchasePrice || 0) : 0) +
      (restaurantType.type === 'existing' && restaurantType.needsRenovations ? (startupCosts.renovationCosts || 0) : 0) +
      (startupCosts.leaseholdImprovements || 0) +
      (startupCosts.kitchenEquipment || 0) +
      (startupCosts.furnitureFixtures || 0) +
      (startupCosts.initialInventory || 0) +
      (startupCosts.preOpeningSalaries || 0) +
      (startupCosts.depositsLicenses || 0) +
      (startupCosts.initialMarketing || 0) +
      (startupCosts.contingency || 0) +
      (startupCosts.signage || 0) +
      (startupCosts.posHardware || 0) +
      (startupCosts.smallwaresStartup || 0) +
      (startupCosts.openingBeverageInventory || 0) +
      (startupCosts.trainingPreOpen || 0) +
      (startupCosts.securityInstall || 0) +
      (startupCosts.hoodVentilation || 0) +
      (startupCosts.plumbingElectrical || 0) +
      (startupCosts.professionalFees || 0) +
      (startupCosts.preOpeningRent || 0) +
      (startupCosts.otherStartup || 0) +
      (startupCosts.realEstateLand || 0) +
      (startupCosts.realEstateBuildings || 0) +
      (startupCosts.equipment || 0) +
      (startupCosts.vehicles || 0) +
      (startupCosts.otherFixedAssets || 0) +
      (startupCosts.prepaidInsurance || 0) +
      (startupCosts.rentDeposits || 0) +
      (startupCosts.utilityDeposits || 0) +
      (startupCosts.supplies || 0) +
      (startupCosts.workingCapitalCashOnHand || 0);

    // Calculate total funding - include ALL funding sources
    const funding = data.fundingSources || {};
    const totalFunding = 
      (funding.personalSavings || 0) +
      (funding.personalAssets || 0) +
      (funding.homeEquity || 0) +
      (funding.retirementFunds || 0) +
      (funding.familyLoans || 0) +
      (funding.friendLoans || 0) +
      (funding.familyGifts || 0) +
      (funding.bankLoans || 0) +
      (funding.sbaLoans || 0) +
      (funding.equipmentFinancing || 0) +
      (funding.lineOfCredit || 0) +
      (funding.businessCreditCards || 0) +
      (funding.crowdfunding || 0) +
      (funding.angelInvestors || 0) +
      (funding.ventureCapital || 0) +
      (funding.grants || 0) +
      (funding.privateInvestors || 0) +
      (funding.businessPartners || 0) +
      (funding.supplierCredit || 0) +
      (funding.otherSources || 0) +
      (funding.ownersEquity || 0) +
      (funding.investorFunds || 0) +
      (funding.otherFunding || 0);

    // Break-even calculation with validation
    const breakEvenRevenue = grossMargin > 0 ? totalExpenses / (grossMargin / 100) : totalExpenses;

    // Cash flow and working capital calculations
    const monthlyBurnRate = totalExpenses / 12;
    const runwayMonths = totalFunding > 0 && monthlyBurnRate > 0 ? totalFunding / monthlyBurnRate : 0;
    const monthsToBreakEven = grossMargin > 0 && monthlyBurnRate > 0 
      ? (totalExpenses / (grossMargin / 100)) / monthlyBurnRate 
      : 0;
    
    // Cash flow needs: 6-month reserve of operating expenses (recommended for initial opening)
    const cashFlowReserve6Months = totalExpenses / 2;
    // Working capital estimate (typically 2-3 months) kept for reference
    const workingCapitalNeeded = monthlyBurnRate * 2.5;
    const totalCapitalNeeded = totalStartupCosts + cashFlowReserve6Months;
    const capitalGap = totalCapitalNeeded - totalFunding;

    // Cash flow projections (simplified)
    const monthlyRevenue = totalRevenueWithOther / 12;
    const monthlyCogs = totalCogs / 12;
    const monthlyGrossProfit = monthlyRevenue - monthlyCogs;
    const monthlyNetCashFlow = monthlyGrossProfit - monthlyBurnRate;

    return {
      totalRevenue: totalRevenueWithOther,
      totalFoodSales,
      totalBeverageSales,
      otherRevenue,
      totalCogs,
      foodCogs,
      beverageCogs,
      grossProfit,
      grossMargin,
      totalOpEx,
      totalPayroll,
      totalYearlyPermits,
      totalMaintenanceCosts,
      totalDebtService,
      totalExpenses,
      netIncome,
      netMargin,
      totalStartupCosts,
      totalFunding,
      breakEvenRevenue,
      fundingGap: totalStartupCosts - totalFunding,
      monthlyBurnRate,
      runwayMonths,
      monthsToBreakEven,
      cashFlowReserve6Months,
      workingCapitalNeeded,
      totalCapitalNeeded,
      capitalGap,
      monthlyRevenue,
      monthlyNetCashFlow,
      // Key ratios
      rentAsPercentOfRevenue: totalRevenueWithOther > 0 ? ((opEx.rent || 0) / totalRevenueWithOther) * 100 : 0,
      laborAsPercentOfRevenue: totalRevenueWithOther > 0 ? (totalPayroll / totalRevenueWithOther) * 100 : 0,
      foodCostPercent: totalFoodSales > 0 ? (foodCogs / totalFoodSales) * 100 : 0,
      beverageCostPercent: totalBeverageSales > 0 ? (beverageCogs / totalBeverageSales) * 100 : 0,
    };
  }, [data, dailySalesProjections, laborProjections, financialAnalysis, fundingAnalysis]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getHealthStatus = (actual, benchmark, higher = false) => {
    const ratio = actual / benchmark;
    if (higher) {
      return ratio >= 1.1 ? 'excellent' : ratio >= 0.9 ? 'good' : 'needs-attention';
    } else {
      return ratio <= 0.9 ? 'excellent' : ratio <= 1.1 ? 'good' : 'needs-attention';
    }
  };

  return (
    <FeatureGate feature="financial_projections">
      <div className="space-y-8">
        {/* Scenario Manager - Create and Compare Multiple Scenarios */}
        <ScenarioManager />
        
        {/* Restaurant Type & Initial Costs Section */}
        <SectionCard title="Restaurant Type & Initial Costs" color="blue">
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Store className="w-5 h-5 mr-2 text-blue-600" />
                Restaurant Type
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <button
                  onClick={() => handleRestaurantTypeChange('type', 'new')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    restaurantType.type === 'new'
                      ? 'border-blue-600 bg-blue-100'
                      : 'border-gray-300 bg-white hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Building2 className={`w-6 h-6 ${restaurantType.type === 'new' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <div className="font-semibold">New Restaurant</div>
                      <div className="text-sm text-gray-600">Building from scratch</div>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleRestaurantTypeChange('type', 'existing')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    restaurantType.type === 'existing'
                      ? 'border-blue-600 bg-blue-100'
                      : 'border-gray-300 bg-white hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Store className={`w-6 h-6 ${restaurantType.type === 'existing' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <div className="font-semibold">Existing Restaurant</div>
                      <div className="text-sm text-gray-600">Purchasing existing location</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* New Restaurant - Build Costs */}
            {restaurantType.type === 'new' && (
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-green-600" />
                  Build Costs
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Total Build Costs"
                    type="number"
                    value={restaurantType.buildCosts || 0}
                    onChange={(value) => handleRestaurantTypeChange('buildCosts', parseFloat(value) || 0)}
                    placeholder="300000"
                    helpText="Includes construction, permits, design, etc."
                  />
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Estimated Breakdown:</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Construction/Leasehold Improvements:</span>
                        <span className="font-semibold">${((restaurantType.buildCosts || 0) * 0.50).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Kitchen Equipment:</span>
                        <span className="font-semibold">${((restaurantType.buildCosts || 0) * 0.25).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Furniture & Fixtures:</span>
                        <span className="font-semibold">${((restaurantType.buildCosts || 0) * 0.15).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Permits & Licenses:</span>
                        <span className="font-semibold">${((restaurantType.buildCosts || 0) * 0.05).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Design & Professional Fees:</span>
                        <span className="font-semibold">${((restaurantType.buildCosts || 0) * 0.05).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Existing Restaurant - Purchase Price & Renovations */}
            {restaurantType.type === 'existing' && (
              <div className="space-y-4">
                <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Store className="w-5 h-5 mr-2 text-purple-600" />
                    Purchase Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Purchase Price"
                      type="number"
                      value={restaurantType.purchasePrice || 0}
                      onChange={(value) => handleRestaurantTypeChange('purchasePrice', parseFloat(value) || 0)}
                      placeholder="500000"
                      helpText="Total purchase price for the existing restaurant"
                    />
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="text-sm text-gray-600 mb-2">Purchase Breakdown:</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Business Assets:</span>
                          <span className="font-semibold">${((restaurantType.purchasePrice || 0) * 0.60).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Equipment & Inventory:</span>
                          <span className="font-semibold">${((restaurantType.purchasePrice || 0) * 0.30).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Goodwill/Brand Value:</span>
                          <span className="font-semibold">${((restaurantType.purchasePrice || 0) * 0.10).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Wrench className="w-5 h-5 mr-2 text-orange-600" />
                    Renovations
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={restaurantType.needsRenovations || false}
                        onChange={(e) => handleRestaurantTypeChange('needsRenovations', e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium">Renovations Required</span>
                    </label>
                    
                    {restaurantType.needsRenovations && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <FormField
                          label="Renovation Costs"
                          type="number"
                          value={restaurantType.renovations || 0}
                          onChange={(value) => handleRestaurantTypeChange('renovations', parseFloat(value) || 0)}
                          placeholder="100000"
                          helpText="Costs for updates, repairs, and improvements"
                        />
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm text-gray-600 mb-2">Typical Renovation Areas:</div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Kitchen Updates:</span>
                              <span className="font-semibold">${((restaurantType.renovations || 0) * 0.40).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Dining Area Refresh:</span>
                              <span className="font-semibold">${((restaurantType.renovations || 0) * 0.30).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Bathroom/Utilities:</span>
                              <span className="font-semibold">${((restaurantType.renovations || 0) * 0.15).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Exterior/Signage:</span>
                              <span className="font-semibold">${((restaurantType.renovations || 0) * 0.15).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Investment Summary */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold mb-4">Total Initial Investment</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Purchase Price</div>
                      <div className="text-2xl font-bold text-purple-600">
                        ${(restaurantType.purchasePrice || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Renovations</div>
                      <div className="text-2xl font-bold text-orange-600">
                        ${(restaurantType.needsRenovations ? (restaurantType.renovations || 0) : 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-500">
                      <div className="text-sm text-gray-600 mb-1">Total Investment</div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${((restaurantType.purchasePrice || 0) + (restaurantType.needsRenovations ? (restaurantType.renovations || 0) : 0)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Header with location context */}
        <SectionCard 
        title={`${data.restaurantDetails?.location || 'Boston'} Restaurant Financial Projections`} 
        description={`Financial modeling tailored for ${data.restaurantDetails?.location || 'Boston'}'s restaurant market with local benchmarks and regulations.`}
        color="blue"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">{data.restaurantDetails?.location || 'Boston'} Market Context</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Avg rent: ${bostonBenchmarks.avgRentPerSqFt}/sq ft/year</li>
              <li>• Target food cost: {(bostonBenchmarks.avgFoodCostPercent * 100).toFixed(0)}%</li>
              <li>• Target labor: {(bostonBenchmarks.avgLaborPercent * 100).toFixed(0)}%</li>
            </ul>
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">Revenue Targets</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Avg check: ${bostonBenchmarks.avgSeatPrice}</li>
              <li>• Table turns: {bostonBenchmarks.avgTurnovers}/day</li>
              <li>• Revenue/seat: ${(bostonBenchmarks.avgRevPerSeat / 1000).toFixed(0)}k/year</li>
            </ul>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-2">Your Performance</h4>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>• Gross margin: {calculations.grossMargin.toFixed(1)}%</li>
              <li>• Net margin: {calculations.netMargin.toFixed(1)}%</li>
              <li>• Break-even: {formatCurrency(calculations.breakEvenRevenue)}</li>
            </ul>
          </div>
        </div>
      </SectionCard>

      {/* Restaurant Operations Configuration */}
      <SectionCard title="Restaurant Operations Configuration" color="purple">
        <p className="text-sm text-gray-600 mb-4">Seats and square footage are linked with <strong>Operations → Facility & Layout</strong>. Location is used for benchmarks and compliance across the app.</p>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">City / Area</label>
          <select
            value={data.restaurantDetails?.location || 'Boston'}
            onChange={(e) => actions.updateFinancialData('restaurantDetails', { ...data.restaurantDetails, location: e.target.value })}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {LOCATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Basic Operations */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Operations</h3>
            <div className="space-y-4">
              <FormField
                label="Total Seats"
                type="number"
                value={data.restaurantOperations?.seats || 50}
                onChange={(value) => {
                  const num = parseInt(value, 10) || 0;
                  handleFieldChange('restaurantOperations', 'seats', num);
                  actions.updateBusinessPlan('operationsPlan', { seatingCapacity: num });
                }}
                placeholder="50"
              />
              <FormField
                label="Square Footage"
                type="number"
                value={data.restaurantDetails?.squareFootage ?? 2000}
                onChange={(value) => {
                  const num = parseInt(value, 10) || 0;
                  actions.updateFinancialData('restaurantDetails', { squareFootage: num });
                  actions.updateBusinessPlan('operationsPlan', { totalSquareFootage: num });
                }}
                placeholder="2000"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hours of Operation</label>
                <div className="grid grid-cols-1 gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                    // Get hours or initialize with default (open by default)
                    const existingHours = data.restaurantOperations?.hoursOfOperation?.[day];
                    // Ensure we have a complete hours object with all properties
                    const hours = existingHours || { closed: false, open: '11:00', close: '22:00' };
                    // Format time to ensure HH:MM format for HTML time input
                    const formatTimeForInput = (time) => {
                      if (!time) return '11:00';
                      if (typeof time === 'string') {
                        // If already in HH:MM format, return as is
                        if (/^\d{2}:\d{2}$/.test(time)) {
                          return time;
                        }
                        // If in H:MM format, pad with zero
                        if (/^\d{1}:\d{2}$/.test(time)) {
                          return '0' + time;
                        }
                      }
                      return time || '11:00';
                    };
                    
                    // Ensure all properties exist even if they're undefined
                    const completeHours = {
                      closed: hours.closed === true,
                      open: formatTimeForInput(hours.open),
                      close: formatTimeForInput(hours.close)
                    };
                    const isClosed = completeHours.closed; // Explicitly check for true
                    
                    return (
                      <div key={day} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <div className="w-24 text-sm font-medium capitalize">{day}:</div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center space-x-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!isClosed}
                              onChange={(e) => {
                                const newClosedValue = !e.target.checked; // true if unchecked (closed), false if checked (open)
                                // When toggling closed, preserve existing open/close times
                                const currentHours = data.restaurantOperations?.hoursOfOperation?.[day] || { 
                                  closed: false, 
                                  open: '11:00', 
                                  close: '22:00' 
                                };
                                handleFieldChange('restaurantOperations', `hoursOfOperation.${day}`, {
                                  closed: newClosedValue,
                                  open: currentHours.open || completeHours.open,
                                  close: currentHours.close || completeHours.close
                                });
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                            />
                            <span className="text-xs text-gray-600 font-medium">Open</span>
                          </label>
                        </div>
                        {!isClosed && (
                          <div className="flex items-center space-x-2 flex-1">
                            <input
                              type="time"
                              value={completeHours.open || '11:00'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                // Get current hours, preserving all existing properties
                                const currentHours = data.restaurantOperations?.hoursOfOperation?.[day] || { 
                                  closed: false, 
                                  open: '11:00', 
                                  close: completeHours.close || '22:00' 
                                };
                                handleFieldChange('restaurantOperations', `hoursOfOperation.${day}`, {
                                  ...currentHours,
                                  open: newValue,
                                  closed: false
                                });
                              }}
                              className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-24"
                            />
                            <span className="text-gray-500 text-sm">to</span>
                            <input
                              type="time"
                              value={completeHours.close || '22:00'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                // Get current hours, preserving all existing properties
                                const currentHours = data.restaurantOperations?.hoursOfOperation?.[day] || { 
                                  closed: false, 
                                  open: completeHours.open || '11:00', 
                                  close: '22:00' 
                                };
                                handleFieldChange('restaurantOperations', `hoursOfOperation.${day}`, {
                                  ...currentHours,
                                  close: newValue,
                                  closed: false
                                });
                              }}
                              className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-24"
                            />
                          </div>
                        )}
                        {isClosed && (
                          <span className="text-sm text-gray-500 italic font-medium">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & Performance */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Pricing & Performance</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Services Offered</label>
                <div className="flex flex-wrap gap-4 mb-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.restaurantOperations?.serviceTypes?.lunch !== false}
                      onChange={(e) => {
                        const currentServiceTypes = data.restaurantOperations?.serviceTypes || { lunch: true, dinner: true, brunch: false };
                        handleFieldChange('restaurantOperations', 'serviceTypes', {
                          ...currentServiceTypes,
                          lunch: e.target.checked
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Lunch</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.restaurantOperations?.serviceTypes?.dinner !== false}
                      onChange={(e) => {
                        const currentServiceTypes = data.restaurantOperations?.serviceTypes || { lunch: true, dinner: true, brunch: false };
                        handleFieldChange('restaurantOperations', 'serviceTypes', {
                          ...currentServiceTypes,
                          dinner: e.target.checked
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Dinner</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.restaurantOperations?.serviceTypes?.brunch === true}
                      onChange={(e) => {
                        const currentServiceTypes = data.restaurantOperations?.serviceTypes || { lunch: true, dinner: true, brunch: false };
                        handleFieldChange('restaurantOperations', 'serviceTypes', {
                          ...currentServiceTypes,
                          brunch: e.target.checked
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Brunch</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Average Check by Meal Period</label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Lunch"
                    type="number"
                    value={data.restaurantOperations?.averageCheck?.lunch || 18}
                    onChange={(value) => {
                      const numValue = value === '' ? 0 : parseFloat(value);
                      if (!isNaN(numValue)) {
                        handleFieldChange('restaurantOperations', 'averageCheck.lunch', numValue);
                      }
                    }}
                    placeholder="18"
                    disabled={data.restaurantOperations?.serviceTypes?.lunch === false}
                  />
                  <FormField
                    label="Dinner"
                    type="number"
                    value={data.restaurantOperations?.averageCheck?.dinner || 32}
                    onChange={(value) => {
                      const numValue = value === '' ? 0 : parseFloat(value);
                      if (!isNaN(numValue)) {
                        handleFieldChange('restaurantOperations', 'averageCheck.dinner', numValue);
                      }
                    }}
                    placeholder="32"
                    disabled={data.restaurantOperations?.serviceTypes?.dinner === false}
                  />
                  <FormField
                    label="Brunch"
                    type="number"
                    value={data.restaurantOperations?.averageCheck?.brunch || 24}
                    onChange={(value) => {
                      const numValue = value === '' ? 0 : parseFloat(value);
                      if (!isNaN(numValue)) {
                        handleFieldChange('restaurantOperations', 'averageCheck.brunch', numValue);
                      }
                    }}
                    placeholder="24"
                    disabled={data.restaurantOperations?.serviceTypes?.brunch !== true}
                  />
                  <FormField
                    label="Beverages"
                    type="number"
                    value={data.restaurantOperations?.averageCheck?.beverages || 8}
                    onChange={(value) => {
                      const numValue = value === '' ? 0 : parseFloat(value);
                      if (!isNaN(numValue)) {
                        handleFieldChange('restaurantOperations', 'averageCheck.beverages', numValue);
                      }
                    }}
                    placeholder="8"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Table Turnover (per meal period)</label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Lunch"
                    type="number"
                    step="0.1"
                    value={data.restaurantOperations?.tableTurnover?.lunch || 1.5}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'tableTurnover.lunch', parseFloat(value))}
                    placeholder="1.5"
                  />
                  <FormField
                    label="Dinner"
                    type="number"
                    step="0.1"
                    value={data.restaurantOperations?.tableTurnover?.dinner || 2.0}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'tableTurnover.dinner', parseFloat(value))}
                    placeholder="2.0"
                  />
                  <FormField
                    label="Brunch"
                    type="number"
                    step="0.1"
                    value={data.restaurantOperations?.tableTurnover?.brunch || 2.5}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'tableTurnover.brunch', parseFloat(value))}
                    placeholder="2.5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Occupancy Rate (0-1)</label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Lunch"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={data.restaurantOperations?.occupancyRate?.lunch || 0.7}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'occupancyRate.lunch', parseFloat(value))}
                    placeholder="0.7"
                  />
                  <FormField
                    label="Dinner"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={data.restaurantOperations?.occupancyRate?.dinner || 0.9}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'occupancyRate.dinner', parseFloat(value))}
                    placeholder="0.9"
                  />
                  <FormField
                    label="Brunch"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={data.restaurantOperations?.occupancyRate?.brunch || 0.8}
                    onChange={(value) => handleFieldChange('restaurantOperations', 'occupancyRate.brunch', parseFloat(value))}
                    placeholder="0.8"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Planning Calculators */}
      <SectionCard title="Planning Calculators" color="indigo">
        <div className="space-y-8">
          {/* Rent Calculator */}
          <div>
            <RentCalculator />
          </div>

          {/* Sales Projections Calculator */}
          <div className="mt-8">
            <SalesProjectionsCalculator baseAnnualRevenue={dailySalesProjections.annual.totalRevenue} />
          </div>

          {/* Management Cost Projector */}
          <div className="mt-8">
            <ManagementCostProjector baseAnnualRevenue={dailySalesProjections.annual.totalRevenue} />
          </div>
        </div>
      </SectionCard>

      {/* Daily Sales Projections */}
      <SectionCard title="Daily Sales Projections" color="green">
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Weekly Revenue:</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(dailySalesProjections.weekly.totalRevenue)}
                </span>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Weekly Covers:</span>
                <span className="text-lg font-bold text-blue-600">
                  {dailySalesProjections.weekly.covers.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Annual Revenue:</span>
                <span className="text-lg font-bold text-purple-600">
                  {formatCurrency(dailySalesProjections.annual.totalRevenue)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Breakdown Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Covers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Food Sales</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beverage Sales</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Check</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dailySalesProjections.daily.map((day) => (
                <tr key={day.day} className={day.totalRevenue === 0 ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{day.day}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {day.totalRevenue === 0 ? 'Closed' : formatCurrency(day.totalRevenue)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{day.covers}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {day.foodSales === 0 ? '-' : formatCurrency(day.foodSales)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {day.beverageSales === 0 ? '-' : formatCurrency(day.beverageSales)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {day.covers === 0 ? '-' : formatCurrency(day.totalRevenue / day.covers)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Meal Period Breakdown */}
        <div className="mt-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Meal Period Breakdown</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h5 className="font-semibold text-orange-800 mb-2">Lunch</h5>
              <div className="text-sm text-orange-700 space-y-1">
                <div>Weekly Covers: {dailySalesProjections.daily.reduce((sum, day) => sum + day.lunchCovers, 0)}</div>
                <div>Weekly Revenue: {formatCurrency(dailySalesProjections.daily.reduce((sum, day) => sum + day.lunchRevenue, 0))}</div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <h5 className="font-semibold text-red-800 mb-2">Dinner</h5>
              <div className="text-sm text-red-700 space-y-1">
                <div>Weekly Covers: {dailySalesProjections.daily.reduce((sum, day) => sum + day.dinnerCovers, 0)}</div>
                <div>Weekly Revenue: {formatCurrency(dailySalesProjections.daily.reduce((sum, day) => sum + day.dinnerRevenue, 0))}</div>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h5 className="font-semibold text-yellow-800 mb-2">Brunch</h5>
              <div className="text-sm text-yellow-700 space-y-1">
                <div>Weekly Covers: {dailySalesProjections.daily.reduce((sum, day) => sum + day.brunchCovers, 0)}</div>
                <div>Weekly Revenue: {formatCurrency(dailySalesProjections.daily.reduce((sum, day) => sum + day.brunchRevenue, 0))}</div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h5 className="font-semibold text-blue-800 mb-2">Beverages</h5>
              <div className="text-sm text-blue-700 space-y-1">
                <div>Weekly Revenue: {formatCurrency(dailySalesProjections.daily.reduce((sum, day) => sum + day.beverageRevenue, 0))}</div>
                <div>Avg per Cover: {formatCurrency(dailySalesProjections.daily.reduce((sum, day) => sum + day.beverageRevenue, 0) / dailySalesProjections.weekly.covers)}</div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Labor Configuration */}
      <SectionCard title="Labor Configuration" color="blue">
        <div className="space-y-8">
          {/* Tax Rates */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Tax Rates</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FormField
                label="Federal Income Tax"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.federalIncomeTax || 0.22}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.federalIncomeTax', parseFloat(value))}
                placeholder="0.15"
              />
              <FormField
                label="State Income Tax"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.stateIncomeTax || 0.05}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.stateIncomeTax', parseFloat(value))}
                placeholder="0.05"
              />
              <FormField
                label="Social Security"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.socialSecurity || 0.062}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.socialSecurity', parseFloat(value))}
                placeholder="0.062"
              />
              <FormField
                label="Medicare"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.medicare || 0.0145}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.medicare', parseFloat(value))}
                placeholder="0.0145"
              />
              <FormField
                label="Unemployment"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.unemployment || 0.006}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.unemployment', parseFloat(value))}
                placeholder="0.03"
              />
              <FormField
                label="Workers Comp"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.workersComp || 0.015}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.workersComp', parseFloat(value))}
                placeholder="0.02"
              />
              <FormField
                label="Benefits Rate"
                type="number"
                step="0.001"
                value={data.operatingExpenses?.labor?.taxRates?.benefitsRate || 0.30}
                onChange={(value) => handleFieldChange('operatingExpenses', 'labor.taxRates.benefitsRate', parseFloat(value))}
                placeholder="0.25"
              />
            </div>
          </div>

          {/* Management Positions */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Management Positions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(data.operatingExpenses?.labor?.management || []).map((employee, index) => (
                <div key={employee.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{employee.name}</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Hourly Rate"
                      type="number"
                      value={employee.hourlyRate}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.management.${index}.hourlyRate`, parseFloat(value))}
                      placeholder="25"
                    />
                    <FormField
                      label="Hours/Week"
                      type="number"
                      value={employee.hoursPerWeek}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.management.${index}.hoursPerWeek`, parseInt(value))}
                      placeholder="50"
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={employee.benefits}
                        onChange={(e) => handleFieldChange('operatingExpenses', `labor.management.${index}.benefits`, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <label className="text-sm">Benefits Eligible</label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Front of House Positions */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Front of House Positions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data.operatingExpenses?.labor?.frontOfHouse || []).map((employee, index) => (
                <div key={employee.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{employee.name}</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Hourly Rate"
                      type="number"
                      value={employee.hourlyRate}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.frontOfHouse.${index}.hourlyRate`, parseFloat(value))}
                      placeholder="15"
                    />
                    <FormField
                      label="Tips/Hour"
                      type="number"
                      value={employee.tips}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.frontOfHouse.${index}.tips`, parseFloat(value))}
                      placeholder="8"
                    />
                    <FormField
                      label="Hours/Week"
                      type="number"
                      value={employee.hoursPerWeek}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.frontOfHouse.${index}.hoursPerWeek`, parseInt(value))}
                      placeholder="35"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Back of House Positions */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Back of House Positions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data.operatingExpenses?.labor?.backOfHouse || []).map((employee, index) => (
                <div key={employee.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{employee.name}</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Hourly Rate"
                      type="number"
                      value={employee.hourlyRate}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.backOfHouse.${index}.hourlyRate`, parseFloat(value))}
                      placeholder="18"
                    />
                    <FormField
                      label="Hours/Week"
                      type="number"
                      value={employee.hoursPerWeek}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.backOfHouse.${index}.hoursPerWeek`, parseInt(value))}
                      placeholder="40"
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={employee.benefits}
                        onChange={(e) => handleFieldChange('operatingExpenses', `labor.backOfHouse.${index}.benefits`, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <label className="text-sm">Benefits Eligible</label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Support Staff */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Support Staff</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data.operatingExpenses?.labor?.support || []).map((employee, index) => (
                <div key={employee.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{employee.name}</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Hourly Rate"
                      type="number"
                      value={employee.hourlyRate}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.support.${index}.hourlyRate`, parseFloat(value))}
                      placeholder="15"
                    />
                    <FormField
                      label="Hours/Week"
                      type="number"
                      value={employee.hoursPerWeek}
                      onChange={(value) => handleFieldChange('operatingExpenses', `labor.support.${index}.hoursPerWeek`, parseInt(value))}
                      placeholder="20"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Detailed Labor Projections */}
      <SectionCard title="Detailed Labor Projections" color="blue">
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Weekly Labor Cost:</span>
                <span className="text-lg font-bold text-blue-600">
                  {formatCurrency(laborProjections.weekly.totalCost)}
                </span>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Annual Labor Cost:</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(laborProjections.annual.totalCost)}
                </span>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Weekly Tips:</span>
                <span className="text-lg font-bold text-orange-600">
                  {formatCurrency(laborProjections.weekly.tips)}
                </span>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Benefits Cost:</span>
                <span className="text-lg font-bold text-purple-600">
                  {formatCurrency(laborProjections.weekly.benefits)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Labor Breakdown by Department */}
        <div className="space-y-6">
          {/* Management */}
          <div>
            <h4 className="text-md font-semibold mb-3 text-gray-700">Management</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours/Week</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weekly Cost</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Annual Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {laborProjections.positions.management.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{position.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">1</td>
                      <td className="px-3 py-2 text-sm text-gray-900">${position.hourlyRate}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{position.hoursPerWeek}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.totalEmployeeCost)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.annualCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Front of House */}
          <div>
            <h4 className="text-md font-semibold mb-3 text-gray-700">Front of House</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tips/Hour</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours/Week</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weekly Cost</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Annual Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {laborProjections.positions.frontOfHouse.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{position.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">1</td>
                      <td className="px-3 py-2 text-sm text-gray-900">${position.hourlyRate}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">${position.tips}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{position.hoursPerWeek}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.totalEmployeeCost)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.annualCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Back of House */}
          <div>
            <h4 className="text-md font-semibold mb-3 text-gray-700">Back of House</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours/Week</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weekly Cost</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Annual Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {laborProjections.positions.backOfHouse.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{position.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">1</td>
                      <td className="px-3 py-2 text-sm text-gray-900">${position.hourlyRate}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{position.hoursPerWeek}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.totalEmployeeCost)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.annualCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Support Staff */}
          <div>
            <h4 className="text-md font-semibold mb-3 text-gray-700">Support Staff</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours/Week</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weekly Cost</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Annual Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {laborProjections.positions.support.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{position.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">1</td>
                      <td className="px-3 py-2 text-sm text-gray-900">${position.hourlyRate}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{position.hoursPerWeek}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.totalEmployeeCost)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(position.annualCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Labor Cost Breakdown */}
        <div className="mt-8">
          <h4 className="text-md font-semibold mb-4 text-gray-700">Labor Cost Breakdown</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <h5 className="font-semibold text-green-800 mb-2">Base Pay</h5>
              <div className="text-sm text-green-700 space-y-1">
                <div>Weekly: {formatCurrency(laborProjections.weekly.basePay)}</div>
                <div>Annual: {formatCurrency(laborProjections.annual.basePay)}</div>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h5 className="font-semibold text-orange-800 mb-2">Tips</h5>
              <div className="text-sm text-orange-700 space-y-1">
                <div>Weekly: {formatCurrency(laborProjections.weekly.tips)}</div>
                <div>Annual: {formatCurrency(laborProjections.annual.tips)}</div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <h5 className="font-semibold text-red-800 mb-2">Employer Taxes</h5>
              <div className="text-sm text-red-700 space-y-1">
                <div>Weekly: {formatCurrency(laborProjections.weekly.employerTaxes)}</div>
                <div>Annual: {formatCurrency(laborProjections.annual.employerTaxes)}</div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h5 className="font-semibold text-purple-800 mb-2">Benefits</h5>
              <div className="text-sm text-purple-700 space-y-1">
                <div>Weekly: {formatCurrency(laborProjections.weekly.benefits)}</div>
                <div>Annual: {formatCurrency(laborProjections.annual.benefits)}</div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Market Trends & Industry Analysis - Premium Feature */}
      <FeatureGate feature="hasAdvancedAnalytics">
        <SectionCard title="Market Trends & Industry Analysis" color="purple">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Industry Benchmarks */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Industry Benchmarks</h3>
            <div className="space-y-4">
              <FormField
                label="Average Revenue per Seat (Annual)"
                type="number"
                value={data.marketTrends?.industryBenchmarks?.avgRevenuePerSeat || bostonBenchmarks.avgRevPerSeat}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgRevenuePerSeat', parseInt(value))}
                placeholder="75000"
              />
              <FormField
                label="Average Food Cost %"
                type="number"
                step="0.1"
                value={data.marketTrends?.industryBenchmarks?.avgFoodCostPercentage || (bostonBenchmarks.avgFoodCostPercent * 100)}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgFoodCostPercentage', parseFloat(value))}
                placeholder="28"
              />
              <FormField
                label="Average Labor Cost %"
                type="number"
                step="0.1"
                value={data.marketTrends?.industryBenchmarks?.avgLaborPercentage || (bostonBenchmarks.avgLaborPercent * 100)}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgLaborPercentage', parseFloat(value))}
                placeholder="32"
              />
              <FormField
                label="Average Occupancy Rate"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={data.marketTrends?.industryBenchmarks?.avgOccupancyRate || 0.75}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgOccupancyRate', parseFloat(value))}
                placeholder="0.75"
              />
              <FormField
                label="Average Check Size"
                type="number"
                value={data.marketTrends?.industryBenchmarks?.avgCheckSize || bostonBenchmarks.avgSeatPrice}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgCheckSize', parseFloat(value))}
                placeholder="25"
              />
              <FormField
                label="Average Table Turnover"
                type="number"
                step="0.1"
                value={data.marketTrends?.industryBenchmarks?.avgTableTurnover || bostonBenchmarks.avgTurnovers}
                onChange={(value) => handleFieldChange('marketTrends', 'industryBenchmarks.avgTableTurnover', parseFloat(value))}
                placeholder="2.0"
              />
            </div>
          </div>

          {/* Performance vs Benchmarks */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Performance vs Industry</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Industry Revenue Target:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(financialAnalysis.industryBenchmarks.revenueTarget)}
                  </span>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Current Revenue:</span>
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(financialAnalysis.industryBenchmarks.currentRevenue)}
                  </span>
                </div>
              </div>
              <div className={`p-4 rounded-lg ${financialAnalysis.industryBenchmarks.performance >= 100 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Performance vs Benchmark:</span>
                  <span className={`text-lg font-bold ${financialAnalysis.industryBenchmarks.performance >= 100 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {financialAnalysis.industryBenchmarks.performance.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Seasonal Projections */}
            <div className="mt-6">
              <h4 className="font-semibold mb-3">Seasonal Revenue Projections</h4>
              <div className="space-y-2">
                {financialAnalysis.seasonalProjections.map((season) => (
                  <div key={season.season} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="capitalize font-medium">{season.season}:</span>
                    <span className="text-sm">
                      {formatCurrency(season.quarterlyRevenue)} 
                      <span className="text-gray-500 ml-2">({(season.factor * 100).toFixed(0)}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Yearly Permits & Compliance Costs */}
      <SectionCard title="Yearly Permits & Compliance Costs" color="orange">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Business Permits */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Business Permits</h3>
            <div className="space-y-3">
              {data.yearlyPermits?.businessPermits ? Object.entries(data.yearlyPermits.businessPermits).map(([permit, cost]) => (
                <FormField
                  key={permit}
                  label={permit.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  type="number"
                  value={cost}
                  onChange={(value) => handleFieldChange('yearlyPermits', `businessPermits.${permit}`, parseInt(value))}
                  placeholder="0"
                />
              )) : <p className="text-gray-500 text-sm">No business permits configured</p>}
            </div>
            <div className="mt-4 p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Business Permits:</span>
                <span className="font-bold text-orange-600">
                  {formatCurrency(financialAnalysis.breakdown.businessPermits)}
                </span>
              </div>
            </div>
          </div>

          {/* Insurance Costs */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Insurance Requirements</h3>
            <div className="space-y-3">
              {data.yearlyPermits?.insuranceCosts ? Object.entries(data.yearlyPermits.insuranceCosts).map(([insurance, cost]) => (
                <FormField
                  key={insurance}
                  label={insurance.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  type="number"
                  value={cost}
                  onChange={(value) => handleFieldChange('yearlyPermits', `insuranceCosts.${insurance}`, parseInt(value))}
                  placeholder="0"
                />
              )) : <p className="text-gray-500 text-sm">No insurance costs configured</p>}
            </div>
            <div className="mt-4 p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Insurance Costs:</span>
                <span className="font-bold text-orange-600">
                  {formatCurrency(financialAnalysis.breakdown.insuranceCosts)}
                </span>
              </div>
            </div>
          </div>

          {/* Professional Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Professional Services</h3>
            <div className="space-y-3">
              {data.yearlyPermits?.professionalServices ? Object.entries(data.yearlyPermits.professionalServices).map(([service, cost]) => (
                <FormField
                  key={service}
                  label={service.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  type="number"
                  value={cost}
                  onChange={(value) => handleFieldChange('yearlyPermits', `professionalServices.${service}`, parseInt(value))}
                  placeholder="0"
                />
              )) : <p className="text-gray-500 text-sm">No professional services configured</p>}
            </div>
            <div className="mt-4 p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Professional Services:</span>
                <span className="font-bold text-orange-600">
                  {formatCurrency(financialAnalysis.breakdown.professionalServices)}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-orange-100 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">Total Yearly Permits & Compliance:</span>
            <span className="text-xl font-bold text-orange-700">
              {formatCurrency(financialAnalysis.totalYearlyPermits)}
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Maintenance & Equipment Costs */}
      <SectionCard title="Maintenance & Equipment Costs" color="red">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Equipment Maintenance */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Equipment Maintenance</h3>
            <div className="space-y-3">
              {data.maintenanceCosts?.equipmentMaintenance ? Object.entries(data.maintenanceCosts.equipmentMaintenance).map(([maintenance, cost]) => (
                <FormField
                  key={maintenance}
                  label={maintenance.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  type="number"
                  value={cost}
                  onChange={(value) => handleFieldChange('maintenanceCosts', `equipmentMaintenance.${maintenance}`, parseInt(value))}
                  placeholder="0"
                />
              )) : <p className="text-gray-500 text-sm">No equipment maintenance configured</p>}
            </div>
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Equipment Maintenance:</span>
                <span className="font-bold text-red-600">
                  {formatCurrency(financialAnalysis.breakdown.equipmentMaintenance)}
                </span>
              </div>
            </div>
          </div>

          {/* Building Maintenance */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Building Maintenance</h3>
            <div className="space-y-3">
              {data.maintenanceCosts?.buildingMaintenance ? Object.entries(data.maintenanceCosts.buildingMaintenance).map(([maintenance, cost]) => (
                <FormField
                  key={maintenance}
                  label={maintenance.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  type="number"
                  value={cost}
                  onChange={(value) => handleFieldChange('maintenanceCosts', `buildingMaintenance.${maintenance}`, parseInt(value))}
                  placeholder="0"
                />
              )) : <p className="text-gray-500 text-sm">No building maintenance configured</p>}
            </div>
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Building Maintenance:</span>
                <span className="font-bold text-red-600">
                  {formatCurrency(financialAnalysis.breakdown.buildingMaintenance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Replacement Schedule */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Equipment Replacement Schedule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.maintenanceCosts?.equipmentReplacement && typeof data.maintenanceCosts.equipmentReplacement === 'object' && !Array.isArray(data.maintenanceCosts.equipmentReplacement) 
              ? Object.entries(data.maintenanceCosts.equipmentReplacement).map(([equipment, equipmentData]) => (
              <div key={equipment} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">{equipment.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Replacement Cost:</span>
                    <span>{formatCurrency(equipmentData?.cost || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Lifespan:</span>
                    <span>{equipmentData?.years || 0} years</span>
                  </div>
                  <FormField
                    label="Annual Reserve"
                    type="number"
                    value={equipmentData?.annualReserve || 0}
                    onChange={(value) => handleFieldChange('maintenanceCosts', `equipmentReplacement.${equipment}.annualReserve`, parseInt(value))}
                    placeholder="0"
                    className="text-sm"
                  />
                </div>
              </div>
            )) : <p className="text-gray-500 text-sm col-span-full">No equipment replacement schedule configured</p>}
          </div>
          <div className="mt-4 p-3 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total Equipment Replacement Reserve:</span>
              <span className="font-bold text-red-600">
                {formatCurrency(financialAnalysis.breakdown.equipmentReplacement)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-red-100 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">Total Annual Maintenance Costs:</span>
            <span className="text-xl font-bold text-red-700">
              {formatCurrency(financialAnalysis.totalMaintenanceCosts)}
            </span>
          </div>
        </div>
        </SectionCard>
      </FeatureGate>

      {/* 5-Year Financial Projections - Premium Feature */}
      <FeatureGate feature="hasAdvancedAnalytics">
        <SectionCard title="5-Year Financial Projections" color="green">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projected Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor Costs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permits & Compliance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Maintenance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Costs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Income</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {financialAnalysis.projections.map((projection) => (
                <tr key={projection.year} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Year {projection.year}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(projection.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(projection.laborCost)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(projection.permitsCost)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(projection.maintenanceCost)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(projection.totalCosts)}</td>
                  <td className={`px-4 py-3 text-sm font-medium ${projection.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(projection.netIncome)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{projection.growthRate.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">5-Year Revenue Growth:</span>
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(financialAnalysis.projections[4].revenue - financialAnalysis.projections[0].revenue)}
              </span>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Average Annual Growth:</span>
              <span className="text-lg font-bold text-blue-600">
                {((financialAnalysis.projections[4].revenue / financialAnalysis.projections[0].revenue) ** (1/4) - 1 * 100).toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Year 5 Net Income:</span>
              <span className={`text-lg font-bold ${financialAnalysis.projections[4].netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(financialAnalysis.projections[4].netIncome)}
              </span>
            </div>
          </div>
        </div>
        </SectionCard>
      </FeatureGate>

      {/* Funding Sources & Debt Service */}
      <SectionCard title="Funding Sources & Debt Service" color="indigo">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Funding Sources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Funding Sources</h3>
            <div className="space-y-4">
              {/* Personal Investment */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">Personal Investment</h4>
                <div className="space-y-3">
                  <FormField
                    label="Personal Savings"
                    type="number"
                    value={data.fundingSources.personalSavings}
                    onChange={(value) => handleFieldChange('fundingSources', 'personalSavings', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Personal Assets"
                    type="number"
                    value={data.fundingSources.personalAssets}
                    onChange={(value) => handleFieldChange('fundingSources', 'personalAssets', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Home Equity"
                    type="number"
                    value={data.fundingSources.homeEquity}
                    onChange={(value) => handleFieldChange('fundingSources', 'homeEquity', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Retirement Funds"
                    type="number"
                    value={data.fundingSources.retirementFunds}
                    onChange={(value) => handleFieldChange('fundingSources', 'retirementFunds', parseInt(value))}
                    placeholder="0"
                  />
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded">
                  <div className="flex justify-between font-semibold">
                    <span>Personal Total:</span>
                    <span className="text-blue-600">{formatCurrency(fundingAnalysis.breakdown.personalFunding)}</span>
                  </div>
                </div>
              </div>

              {/* Family & Friends */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">Family & Friends</h4>
                <div className="space-y-3">
                  <FormField
                    label="Family Loans"
                    type="number"
                    value={data.fundingSources.familyLoans}
                    onChange={(value) => handleFieldChange('fundingSources', 'familyLoans', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Friend Loans"
                    type="number"
                    value={data.fundingSources.friendLoans}
                    onChange={(value) => handleFieldChange('fundingSources', 'friendLoans', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Family Gifts"
                    type="number"
                    value={data.fundingSources.familyGifts}
                    onChange={(value) => handleFieldChange('fundingSources', 'familyGifts', parseInt(value))}
                    placeholder="0"
                  />
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded">
                  <div className="flex justify-between font-semibold">
                    <span>Family & Friends Total:</span>
                    <span className="text-green-600">{formatCurrency(fundingAnalysis.breakdown.familyFriendsFunding)}</span>
                  </div>
                </div>
              </div>

              {/* Traditional Financing */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">Traditional Financing</h4>
                <div className="space-y-3">
                  <FormField
                    label="Bank Loans"
                    type="number"
                    value={data.fundingSources.bankLoans}
                    onChange={(value) => handleFieldChange('fundingSources', 'bankLoans', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="SBA Loans"
                    type="number"
                    value={data.fundingSources.sbaLoans}
                    onChange={(value) => handleFieldChange('fundingSources', 'sbaLoans', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Equipment Financing"
                    type="number"
                    value={data.fundingSources.equipmentFinancing}
                    onChange={(value) => handleFieldChange('fundingSources', 'equipmentFinancing', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Line of Credit"
                    type="number"
                    value={data.fundingSources.lineOfCredit}
                    onChange={(value) => handleFieldChange('fundingSources', 'lineOfCredit', parseInt(value))}
                    placeholder="0"
                  />
                  <FormField
                    label="Business Credit Cards"
                    type="number"
                    value={data.fundingSources.businessCreditCards}
                    onChange={(value) => handleFieldChange('fundingSources', 'businessCreditCards', parseInt(value))}
                    placeholder="0"
                  />
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded">
                  <div className="flex justify-between font-semibold">
                    <span>Traditional Financing Total:</span>
                    <span className="text-purple-600">{formatCurrency(fundingAnalysis.breakdown.traditionalFinancing)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Alternative & Other Funding + Debt Service */}
          <div>
            {/* Alternative Financing */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Alternative & Other Funding</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">Alternative Financing</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Crowdfunding"
                      type="number"
                      value={data.fundingSources.crowdfunding}
                      onChange={(value) => handleFieldChange('fundingSources', 'crowdfunding', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Angel Investors"
                      type="number"
                      value={data.fundingSources.angelInvestors}
                      onChange={(value) => handleFieldChange('fundingSources', 'angelInvestors', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Venture Capital"
                      type="number"
                      value={data.fundingSources.ventureCapital}
                      onChange={(value) => handleFieldChange('fundingSources', 'ventureCapital', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Grants"
                      type="number"
                      value={data.fundingSources.grants}
                      onChange={(value) => handleFieldChange('fundingSources', 'grants', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Private Investors"
                      type="number"
                      value={data.fundingSources.privateInvestors}
                      onChange={(value) => handleFieldChange('fundingSources', 'privateInvestors', parseInt(value))}
                      placeholder="0"
                    />
                  </div>
                  <div className="mt-3 p-2 bg-gray-50 rounded">
                    <div className="flex justify-between font-semibold">
                      <span>Alternative Total:</span>
                      <span className="text-orange-600">{formatCurrency(fundingAnalysis.breakdown.alternativeFinancing)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">Other Sources</h4>
                  <div className="space-y-3">
                    <FormField
                      label="Business Partners"
                      type="number"
                      value={data.fundingSources.businessPartners}
                      onChange={(value) => handleFieldChange('fundingSources', 'businessPartners', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Supplier Credit"
                      type="number"
                      value={data.fundingSources.supplierCredit}
                      onChange={(value) => handleFieldChange('fundingSources', 'supplierCredit', parseInt(value))}
                      placeholder="0"
                    />
                    <FormField
                      label="Other Sources"
                      type="number"
                      value={data.fundingSources.otherSources}
                      onChange={(value) => handleFieldChange('fundingSources', 'otherSources', parseInt(value))}
                      placeholder="0"
                    />
                  </div>
                  <div className="mt-3 p-2 bg-gray-50 rounded">
                    <div className="flex justify-between font-semibold">
                      <span>Other Sources Total:</span>
                      <span className="text-red-600">{formatCurrency(fundingAnalysis.breakdown.otherFunding)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Debt Service Summary */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Debt Service Analysis</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm font-medium text-blue-600">Monthly Debt Service</div>
                    <div className="text-xl font-bold text-blue-700">{formatCurrency(fundingAnalysis.totalMonthlyDebtService)}</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="text-sm font-medium text-purple-600">Annual Debt Service</div>
                    <div className="text-xl font-bold text-purple-700">{formatCurrency(fundingAnalysis.totalAnnualDebtService)}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-800">Monthly Payments by Source:</h4>
                  {Object.entries(fundingAnalysis.debtService).map(([source, payment]) => (
                    payment > 0 && (
                      <div key={source} className="flex justify-between p-2 bg-gray-50 rounded">
                        <span className="capitalize">{source.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="font-semibold">{formatCurrency(payment)}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Funding Summary */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total Funding:</span>
              <span className="text-xl font-bold text-indigo-600">
                {formatCurrency(fundingAnalysis.totalFunding)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total Startup Costs:</span>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(fundingAnalysis.totalStartupCosts)}
              </span>
            </div>
          </div>
          <div className={`p-4 rounded-lg ${fundingAnalysis.fundingGap >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold">Funding Gap:</span>
              <span className={`text-xl font-bold ${fundingAnalysis.fundingGap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(fundingAnalysis.fundingGap)}
              </span>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Revenue Projections */}
      <SectionCard title="Revenue Projections (Year 1)" color="green">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Food Sales (Annual)"
            type="number"
            value={data.revenue.foodSales}
            onChange={(value) => handleFieldChange('revenue', 'foodSales', value)}
            placeholder="500000"
          />
          <FormField
            label="Beverage Sales (Annual)"
            type="number"
            value={data.revenue.beverageSales}
            onChange={(value) => handleFieldChange('revenue', 'beverageSales', value)}
            placeholder="150000"
          />
          <FormField
            label="Catering Sales (Annual)"
            type="number"
            value={data.revenue.cateringSales}
            onChange={(value) => handleFieldChange('revenue', 'cateringSales', value)}
            placeholder="25000"
          />
          <FormField
            label="Other Revenue (Annual)"
            type="number"
            value={data.revenue.otherRevenue}
            onChange={(value) => handleFieldChange('revenue', 'otherRevenue', value)}
            placeholder="10000"
          />
        </div>
        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">Total Annual Revenue:</span>
            <span className="text-2xl font-bold text-green-600">
              {formatCurrency(calculations.totalRevenue)}
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Cost of Goods Sold */}
      <SectionCard title="Cost of Goods Sold (COGS)" color="orange">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Food COGS % (Boston avg: 28%)"
            type="number"
            value={data.cogs.foodCogsPercent * 100}
            onChange={(value) => handleFieldChange('cogs', 'foodCogsPercent', value / 100)}
            placeholder="28"
            step="0.1"
          />
          <FormField
            label="Beverage COGS % (Target: 20-25%)"
            type="number"
            value={data.cogs.beverageCogsPercent * 100}
            onChange={(value) => handleFieldChange('cogs', 'beverageCogsPercent', value / 100)}
            placeholder="22"
            step="0.1"
          />
          <FormField
            label="Merchandise COGS % (Target: 15-20%)"
            type="number"
            value={data.cogs.merchandiseCogsPercent * 100}
            onChange={(value) => handleFieldChange('cogs', 'merchandiseCogsPercent', value / 100)}
            placeholder="15"
            step="0.1"
          />
          <FormField
            label="Catering COGS % (Target: 30-35%)"
            type="number"
            value={data.cogs.cateringCogsPercent * 100}
            onChange={(value) => handleFieldChange('cogs', 'cateringCogsPercent', value / 100)}
            placeholder="32"
            step="0.1"
          />
          <FormField
            label="Other COGS % (Target: 10-15%)"
            type="number"
            value={data.cogs.otherCogsPercent * 100}
            onChange={(value) => handleFieldChange('cogs', 'otherCogsPercent', value / 100)}
            placeholder="12"
            step="0.1"
          />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total COGS:</span>
              <span className="text-xl font-bold text-orange-600">
                {formatCurrency(calculations.totalCogs)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Gross Profit:</span>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(calculations.grossProfit)}
              </span>
            </div>
          </div>
        </div>
        
        {/* AI Assistant for COGS */}
        <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex items-center space-x-2 mb-3">
            <Sparkles className="w-5 h-5 text-orange-600" />
            <h4 className="font-semibold text-gray-900">AI COGS Optimizer</h4>
          </div>
          <AIAssistant
            context={{
              cogs: data.cogs,
              totalCogs: calculations.totalCogs,
              grossProfit: calculations.grossProfit,
              grossMargin: calculations.grossMargin,
              foodCostPercent: calculations.foodCostPercent,
              beverageCostPercent: calculations.beverageCostPercent,
              revenue: data.revenue,
              bostonBenchmarks: bostonBenchmarks
            }}
            section="cogs"
            placeholder="Ask: 'Are my food costs too high?', 'How can I reduce my COGS?', 'What are industry-standard COGS percentages?'..."
            showQuickActions={false}
          />
        </div>
      </SectionCard>

      {/* Monthly Revenue & Expenses (Chart of Accounts) - Opening projections; used in Monthly Statement for actual vs plan */}
      <SectionCard
        title="Opening projections: Monthly P&amp;L by account"
        description="Estimate monthly revenue and expenses by line item (chart of accounts). These numbers are your opening plan—once you're open, enter actuals in the Monthly Financial Statement (bottom of this page) to compare to plan."
        color="teal"
      >
        <div className="space-y-6">
          {MONTHLY_PL_SECTIONS.map((section) => {
            const sectionData = monthlyPL[section.id] || {};
            const sectionTotal = section.lines.reduce((sum, { code }) => sum + (Number(sectionData[code]) || 0), 0);
            return (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">{section.title}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {section.lines.map(({ code, label }) => (
                    <FormField
                      key={code}
                      label={`${code} ${label}`}
                      type="number"
                      value={sectionData[code] === 0 ? '' : sectionData[code]}
                      onChange={(value) => handleMonthlyPLChange(section.id, code, value)}
                      placeholder="0"
                    />
                  ))}
                </div>
                <p className="text-sm font-medium text-gray-700 mt-2">{section.totalLabel}: {sectionTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
              </div>
            );
          })}
          {/* Summary totals */}
          {(() => {
            const totalRevenue = (monthlyPL.revenue && Object.values(monthlyPL.revenue).reduce((s, v) => s + (Number(v) || 0), 0)) || 0;
            const totalCOGS = (monthlyPL.cogs && Object.values(monthlyPL.cogs).reduce((s, v) => s + (Number(v) || 0), 0)) || 0;
            const grossProfit = totalRevenue - totalCOGS;
            const expenseSectionIds = ['labor', 'directOperating', 'transaction', 'marketing', 'generalAdmin', 'travelMeals', 'repairsMaintenance', 'property'];
            const totalExpenses = expenseSectionIds.reduce((sum, id) => {
              const section = MONTHLY_PL_SECTIONS.find((s) => s.id === id);
              if (!section) return sum;
              const sectionData = monthlyPL[id] || {};
              return sum + section.lines.reduce((s, { code }) => s + (Number(sectionData[code]) || 0), 0);
            }, 0);
            const netOperatingIncome = grossProfit - totalExpenses;
            const totalOtherIncome = (monthlyPL.otherIncome && Object.values(monthlyPL.otherIncome).reduce((s, v) => s + (Number(v) || 0), 0)) || 0;
            const totalOtherExpenses = (monthlyPL.otherExpenses && Object.values(monthlyPL.otherExpenses).reduce((s, v) => s + (Number(v) || 0), 0)) || 0;
            const netOtherIncome = totalOtherIncome - totalOtherExpenses;
            const netIncome = netOperatingIncome + netOtherIncome;
            const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            return (
              <div className="border-2 border-teal-200 rounded-lg p-4 bg-teal-50/50 space-y-1 text-sm">
                <div className="font-semibold text-gray-800">Summary (monthly)</div>
                <div>Total Income: {fmt(totalRevenue)}</div>
                <div>Total Cost of Goods Sold: {fmt(totalCOGS)}</div>
                <div className="font-medium">Gross Profit: {fmt(grossProfit)}</div>
                <div>Total Expenses: {fmt(totalExpenses)}</div>
                <div className="font-medium">Net Operating Income: {fmt(netOperatingIncome)}</div>
                <div>Total Other Income: {fmt(totalOtherIncome)}</div>
                <div>Total Other Expenses: {fmt(totalOtherExpenses)}</div>
                <div className="font-medium">Net Other Income: {fmt(netOtherIncome)}</div>
                <div className="font-semibold text-teal-800">Net Income: {fmt(netIncome)}</div>
              </div>
            );
          })()}
        </div>
      </SectionCard>

      {/* Operating Expenses */}
      <SectionCard title="Boston Operating Expenses (Annual)" color="red">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Rent (Boston avg: $45/sq ft)"
            type="number"
            value={data.operatingExpenses.rent}
            onChange={(value) => handleFieldChange('operatingExpenses', 'rent', value)}
            placeholder="120000"
          />
          <FormField
            label={`Utilities (${data.restaurantDetails?.location || 'Boston'} avg: $8/sq ft)`}
            type="number"
            value={data.operatingExpenses.utilities}
            onChange={(value) => handleFieldChange('operatingExpenses', 'utilities', value)}
            placeholder="18000"
          />
          <FormField
            label="Insurance (Required in MA)"
            type="number"
            value={data.operatingExpenses.insurance}
            onChange={(value) => handleFieldChange('operatingExpenses', 'insurance', value)}
            placeholder="15000"
          />
          <FormField
            label="Marketing & Advertising"
            type="number"
            value={data.operatingExpenses.marketing}
            onChange={(value) => handleFieldChange('operatingExpenses', 'marketing', value)}
            placeholder="25000"
          />
          <FormField
            label="Legal & Accounting"
            type="number"
            value={data.operatingExpenses.legalAccounting}
            onChange={(value) => handleFieldChange('operatingExpenses', 'legalAccounting', value)}
            placeholder="8000"
          />
          <FormField
            label="Repairs & Maintenance"
            type="number"
            value={data.operatingExpenses.repairsMaintenance}
            onChange={(value) => handleFieldChange('operatingExpenses', 'repairsMaintenance', value)}
            placeholder="12000"
          />
        </div>

        {/* Detailed Operational Expenses */}
        <h3 className="text-lg font-semibold mt-8 mb-4">Detailed Operational Expenses</h3>
        
        {/* Cleaning & Maintenance Services */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Cleaning & Maintenance Services</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Linen Service (Tablecloths, Napkins)"
              type="number"
              value={data.operatingExpenses.linenService}
              onChange={(value) => handleFieldChange('operatingExpenses', 'linenService', value)}
              placeholder="3600"
            />
            <FormField
              label="Trash Collection"
              type="number"
              value={data.operatingExpenses.trashCollection}
              onChange={(value) => handleFieldChange('operatingExpenses', 'trashCollection', value)}
              placeholder="2400"
            />
            <FormField
              label="Compost Service"
              type="number"
              value={data.operatingExpenses.compostService}
              onChange={(value) => handleFieldChange('operatingExpenses', 'compostService', value)}
              placeholder="1800"
            />
            <FormField
              label="Professional Cleaning Service"
              type="number"
              value={data.operatingExpenses.cleaningService}
              onChange={(value) => handleFieldChange('operatingExpenses', 'cleaningService', value)}
              placeholder="12000"
            />
            <FormField
              label="Hood Cleaning (Monthly)"
              type="number"
              value={data.operatingExpenses.hoodCleaning}
              onChange={(value) => handleFieldChange('operatingExpenses', 'hoodCleaning', value)}
              placeholder="2400"
            />
            <FormField
              label="Grease Trap Cleaning"
              type="number"
              value={data.operatingExpenses.greaseTrapCleaning}
              onChange={(value) => handleFieldChange('operatingExpenses', 'greaseTrapCleaning', value)}
              placeholder="1200"
            />
          </div>
        </div>

        {/* Technology & Communications */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Technology & Communications</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Internet & Phone"
              type="number"
              value={data.operatingExpenses.internetPhone}
              onChange={(value) => handleFieldChange('operatingExpenses', 'internetPhone', value)}
              placeholder="3600"
            />
            <FormField
              label="Security System"
              type="number"
              value={data.operatingExpenses.securitySystem}
              onChange={(value) => handleFieldChange('operatingExpenses', 'securitySystem', value)}
              placeholder="2400"
            />
            <FormField
              label="Point of Sale System"
              type="number"
              value={data.operatingExpenses.pointOfSale}
              onChange={(value) => handleFieldChange('operatingExpenses', 'pointOfSale', value)}
              placeholder="4800"
            />
            <FormField
              label="Credit Card Processing Fees"
              type="number"
              value={data.operatingExpenses.creditCardProcessing}
              onChange={(value) => handleFieldChange('operatingExpenses', 'creditCardProcessing', value)}
              placeholder="15000"
            />
            <FormField
              label="Software Subscriptions"
              type="number"
              value={data.operatingExpenses.softwareSubscriptions}
              onChange={(value) => handleFieldChange('operatingExpenses', 'softwareSubscriptions', value)}
              placeholder="2400"
            />
            <FormField
              label="Technology Support"
              type="number"
              value={data.operatingExpenses.technologySupport}
              onChange={(value) => handleFieldChange('operatingExpenses', 'technologySupport', value)}
              placeholder="1800"
            />
          </div>
        </div>

        {/* Supplies & Equipment */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Supplies & Equipment</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Cleaning Supplies"
              type="number"
              value={data.operatingExpenses.cleaningSupplies}
              onChange={(value) => handleFieldChange('operatingExpenses', 'cleaningSupplies', value)}
              placeholder="4800"
            />
            <FormField
              label="Paper Goods (Napkins, Toilet Paper)"
              type="number"
              value={data.operatingExpenses.paperGoods}
              onChange={(value) => handleFieldChange('operatingExpenses', 'paperGoods', value)}
              placeholder="3600"
            />
            <FormField
              label="Kitchen Supplies"
              type="number"
              value={data.operatingExpenses.kitchenSupplies}
              onChange={(value) => handleFieldChange('operatingExpenses', 'kitchenSupplies', value)}
              placeholder="6000"
            />
            <FormField
              label="Dishware Replacement"
              type="number"
              value={data.operatingExpenses.dishwareReplacement}
              onChange={(value) => handleFieldChange('operatingExpenses', 'dishwareReplacement', value)}
              placeholder="3000"
            />
            <FormField
              label="Equipment Maintenance"
              type="number"
              value={data.operatingExpenses.equipmentMaintenance}
              onChange={(value) => handleFieldChange('operatingExpenses', 'equipmentMaintenance', value)}
              placeholder="6000"
            />
            <FormField
              label="Equipment Rental"
              type="number"
              value={data.operatingExpenses.equipmentRental}
              onChange={(value) => handleFieldChange('operatingExpenses', 'equipmentRental', value)}
              placeholder="0"
            />
          </div>
        </div>

        {/* Employee Services */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Employee Services & Benefits</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Uniform Service"
              type="number"
              value={data.operatingExpenses.uniformService}
              onChange={(value) => handleFieldChange('operatingExpenses', 'uniformService', value)}
              placeholder="2400"
            />
            <FormField
              label="Uniform Laundry"
              type="number"
              value={data.operatingExpenses.uniformLaundry}
              onChange={(value) => handleFieldChange('operatingExpenses', 'uniformLaundry', value)}
              placeholder="1800"
            />
            <FormField
              label="Employee Benefits"
              type="number"
              value={data.operatingExpenses.employeeBenefits}
              onChange={(value) => handleFieldChange('operatingExpenses', 'employeeBenefits', value)}
              placeholder="15000"
            />
            <FormField
              label="Workers Compensation"
              type="number"
              value={data.operatingExpenses.workersCompensation}
              onChange={(value) => handleFieldChange('operatingExpenses', 'workersCompensation', value)}
              placeholder="8000"
            />
            <FormField
              label="Unemployment Insurance"
              type="number"
              value={data.operatingExpenses.unemploymentInsurance}
              onChange={(value) => handleFieldChange('operatingExpenses', 'unemploymentInsurance', value)}
              placeholder="4000"
            />
            <FormField
              label="Health Insurance"
              type="number"
              value={data.operatingExpenses.healthInsurance}
              onChange={(value) => handleFieldChange('operatingExpenses', 'healthInsurance', value)}
              placeholder="24000"
            />
            <FormField
              label="Retirement Benefits"
              type="number"
              value={data.operatingExpenses.retirementBenefits}
              onChange={(value) => handleFieldChange('operatingExpenses', 'retirementBenefits', value)}
              placeholder="12000"
            />
            <FormField
              label="Training & Certification"
              type="number"
              value={data.operatingExpenses.trainingCertification}
              onChange={(value) => handleFieldChange('operatingExpenses', 'trainingCertification', value)}
              placeholder="3000"
            />
            <FormField
              label="Temporary Staffing"
              type="number"
              value={data.operatingExpenses.temporaryStaffing}
              onChange={(value) => handleFieldChange('operatingExpenses', 'temporaryStaffing', value)}
              placeholder="0"
            />
          </div>
        </div>

        {/* Business Operations */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Business Operations</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Pest Control"
              type="number"
              value={data.operatingExpenses.pestControl}
              onChange={(value) => handleFieldChange('operatingExpenses', 'pestControl', value)}
              placeholder="1800"
            />
            <FormField
              label="Waste Management"
              type="number"
              value={data.operatingExpenses.wasteManagement}
              onChange={(value) => handleFieldChange('operatingExpenses', 'wasteManagement', value)}
              placeholder="2400"
            />
            <FormField
              label="Permits & Licenses"
              type="number"
              value={data.operatingExpenses.permitsLicenses}
              onChange={(value) => handleFieldChange('operatingExpenses', 'permitsLicenses', value)}
              placeholder="3000"
            />
            <FormField
              label="Music Licensing"
              type="number"
              value={data.operatingExpenses.musicLicensing}
              onChange={(value) => handleFieldChange('operatingExpenses', 'musicLicensing', value)}
              placeholder="1200"
            />
            <FormField
              label="Delivery Service Fees"
              type="number"
              value={data.operatingExpenses.deliveryServiceFees}
              onChange={(value) => handleFieldChange('operatingExpenses', 'deliveryServiceFees', value)}
              placeholder="15000"
            />
            <FormField
              label="Bank Fees"
              type="number"
              value={data.operatingExpenses.bankFees}
              onChange={(value) => handleFieldChange('operatingExpenses', 'bankFees', value)}
              placeholder="1200"
            />
            <FormField
              label="Parking Fees"
              type="number"
              value={data.operatingExpenses.parkingFees}
              onChange={(value) => handleFieldChange('operatingExpenses', 'parkingFees', value)}
              placeholder="3600"
            />
            <FormField
              label="Storage Fees"
              type="number"
              value={data.operatingExpenses.storageFees}
              onChange={(value) => handleFieldChange('operatingExpenses', 'storageFees', value)}
              placeholder="0"
            />
            <FormField
              label="Professional Services"
              type="number"
              value={data.operatingExpenses.professionalServices}
              onChange={(value) => handleFieldChange('operatingExpenses', 'professionalServices', value)}
              placeholder="6000"
            />
          </div>
        </div>

        {/* Taxes & Compliance */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Taxes & Compliance</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Local Taxes"
              type="number"
              value={data.operatingExpenses.localTaxes}
              onChange={(value) => handleFieldChange('operatingExpenses', 'localTaxes', value)}
              placeholder="5000"
            />
            <FormField
              label="Property Taxes"
              type="number"
              value={data.operatingExpenses.propertyTaxes}
              onChange={(value) => handleFieldChange('operatingExpenses', 'propertyTaxes', value)}
              placeholder="8000"
            />
            <FormField
              label="Business Taxes"
              type="number"
              value={data.operatingExpenses.businessTaxes}
              onChange={(value) => handleFieldChange('operatingExpenses', 'businessTaxes', value)}
              placeholder="12000"
            />
            <FormField
              label="Safety Training"
              type="number"
              value={data.operatingExpenses.safetyTraining}
              onChange={(value) => handleFieldChange('operatingExpenses', 'safetyTraining', value)}
              placeholder="2400"
            />
            <FormField
              label="Quality Control"
              type="number"
              value={data.operatingExpenses.qualityControl}
              onChange={(value) => handleFieldChange('operatingExpenses', 'qualityControl', value)}
              placeholder="3000"
            />
            <FormField
              label="Inventory Management"
              type="number"
              value={data.operatingExpenses.inventoryManagement}
              onChange={(value) => handleFieldChange('operatingExpenses', 'inventoryManagement', value)}
              placeholder="1800"
            />
          </div>
        </div>

        {/* Miscellaneous */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Miscellaneous</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Consulting"
              type="number"
              value={data.operatingExpenses.consulting}
              onChange={(value) => handleFieldChange('operatingExpenses', 'consulting', value)}
              placeholder="5000"
            />
            <FormField
              label="Travel & Entertainment"
              type="number"
              value={data.operatingExpenses.travelEntertainment}
              onChange={(value) => handleFieldChange('operatingExpenses', 'travelEntertainment', value)}
              placeholder="3000"
            />
            <FormField
              label="Charitable Donations"
              type="number"
              value={data.operatingExpenses.charitableDonations}
              onChange={(value) => handleFieldChange('operatingExpenses', 'charitableDonations', value)}
              placeholder="2000"
            />
          </div>
        </div>

        {/* Financial & Accounting */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Financial & Accounting</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Depreciation"
              type="number"
              value={data.operatingExpenses.depreciation}
              onChange={(value) => handleFieldChange('operatingExpenses', 'depreciation', value)}
              placeholder="15000"
              helpText="Annual depreciation of equipment and assets"
            />
            <FormField
              label="Amortization"
              type="number"
              value={data.operatingExpenses.amortization}
              onChange={(value) => handleFieldChange('operatingExpenses', 'amortization', value)}
              placeholder="5000"
              helpText="Annual amortization of intangible assets"
            />
            <FormField
              label="Interest Expense"
              type="number"
              value={data.operatingExpenses.interestExpense || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'interestExpense', value)}
              placeholder="8000"
              helpText="Interest on loans and credit lines"
            />
            <FormField
              label="Loan Fees"
              type="number"
              value={data.operatingExpenses.loanFees || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'loanFees', value)}
              placeholder="2000"
              helpText="Annual loan origination and servicing fees"
            />
          </div>
        </div>

        {/* Additional Operating Costs */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3 text-gray-700">Additional Operating Costs</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField
              label="Vehicle Expenses"
              type="number"
              value={data.operatingExpenses.vehicleExpenses || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'vehicleExpenses', value)}
              placeholder="6000"
              helpText="Delivery vehicles, fuel, maintenance"
            />
            <FormField
              label="Menu Printing & Design"
              type="number"
              value={data.operatingExpenses.menuPrinting || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'menuPrinting', value)}
              placeholder="2400"
              helpText="Menu updates, printing, design costs"
            />
            <FormField
              label="Website Maintenance"
              type="number"
              value={data.operatingExpenses.websiteMaintenance || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'websiteMaintenance', value)}
              placeholder="1800"
              helpText="Website hosting, updates, maintenance"
            />
            <FormField
              label="Email Marketing Services"
              type="number"
              value={data.operatingExpenses.emailMarketing || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'emailMarketing', value)}
              placeholder="1200"
              helpText="Email marketing platform subscriptions"
            />
            <FormField
              label="Social Media Management"
              type="number"
              value={data.operatingExpenses.socialMediaManagement || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'socialMediaManagement', value)}
              placeholder="3600"
              helpText="Social media tools and services"
            />
            <FormField
              label="Photography & Videography"
              type="number"
              value={data.operatingExpenses.photography || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'photography', value)}
              placeholder="3000"
              helpText="Food photography, menu photos, marketing videos"
            />
            <FormField
              label="Reservation System Fees"
              type="number"
              value={data.operatingExpenses.reservationSystem || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'reservationSystem', value)}
              placeholder="2400"
              helpText="Reservation booking platform fees"
            />
            <FormField
              label="Gift Card Processing Fees"
              type="number"
              value={data.operatingExpenses.giftCardFees || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'giftCardFees', value)}
              placeholder="1800"
              helpText="Gift card platform and processing fees"
            />
            <FormField
              label="Third-Party Delivery Platform Fees"
              type="number"
              value={data.operatingExpenses.thirdPartyDeliveryFees || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'thirdPartyDeliveryFees', value)}
              placeholder="20000"
              helpText="DoorDash, Uber Eats, Grubhub commissions (separate from delivery service fees)"
            />
            <FormField
              label="Employee Meals"
              type="number"
              value={data.operatingExpenses.employeeMeals || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'employeeMeals', value)}
              placeholder="6000"
              helpText="Cost of employee meals during shifts"
            />
            <FormField
              label="Smallwares & Kitchen Tools"
              type="number"
              value={data.operatingExpenses.smallwares || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'smallwares', value)}
              placeholder="3000"
              helpText="Utensils, small equipment, kitchen tools replacement"
            />
            <FormField
              label="Event Hosting Costs"
              type="number"
              value={data.operatingExpenses.eventHosting || 0}
              onChange={(value) => handleFieldChange('operatingExpenses', 'eventHosting', value)}
              placeholder="5000"
              helpText="Private events, catering setup costs"
            />
          </div>
        </div>

        <h3 className="text-lg font-semibold mt-8 mb-4">Boston Payroll (MA min wage: $15/hr)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Owner/Manager Salary"
            type="number"
            value={data.operatingExpenses.salaryOwners}
            onChange={(value) => handleFieldChange('operatingExpenses', 'salaryOwners', value)}
            placeholder="75000"
          />
          <FormField
            label="Full-Time Staff (Annual)"
            type="number"
            value={data.operatingExpenses.salaryFullTime}
            onChange={(value) => handleFieldChange('operatingExpenses', 'salaryFullTime', value)}
            placeholder="180000"
          />
          <FormField
            label="Part-Time Staff (Annual)"
            type="number"
            value={data.operatingExpenses.salaryPartTime}
            onChange={(value) => handleFieldChange('operatingExpenses', 'salaryPartTime', value)}
            placeholder="120000"
          />
          <FormField
            label="Payroll Tax Rate (MA: ~12%)"
            type="number"
            value={data.operatingExpenses.payrollTaxRate * 100}
            onChange={(value) => handleFieldChange('operatingExpenses', 'payrollTaxRate', value / 100)}
            placeholder="12"
            step="0.1"
          />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Operating Expenses:</span>
              <span className="text-lg font-bold text-red-600">
                {formatCurrency(calculations.totalOpEx)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total Payroll:</span>
              <span className="text-lg font-bold text-purple-600">
                {formatCurrency(calculations.totalPayroll)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Permits & Compliance:</span>
              <span className="text-lg font-bold text-orange-600">
                {formatCurrency(calculations.totalYearlyPermits)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Maintenance:</span>
              <span className="text-lg font-bold text-red-600">
                {formatCurrency(calculations.totalMaintenanceCosts)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Debt Service:</span>
              <span className="text-lg font-bold text-indigo-600">
                {formatCurrency(calculations.totalDebtService)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Net Income:</span>
              <span className={`text-lg font-bold ${calculations.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(calculations.netIncome)}
              </span>
            </div>
          </div>
        </div>
        
        {/* AI Assistant for Operating Expenses */}
        <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center space-x-2 mb-3">
            <Sparkles className="w-5 h-5 text-red-600" />
            <h4 className="font-semibold text-gray-900">AI Expense Optimizer</h4>
          </div>
          <AIAssistant
            context={{
              operatingExpenses: data.operatingExpenses,
              totalOpEx: calculations.totalOpEx,
              rentAsPercentOfRevenue: calculations.rentAsPercentOfRevenue,
              laborAsPercentOfRevenue: calculations.laborAsPercentOfRevenue,
              totalRevenue: calculations.totalRevenue,
              bostonBenchmarks: bostonBenchmarks,
              restaurantOperations: data.restaurantOperations
            }}
            section="operatingExpenses"
            placeholder="Ask: 'Are my operating expenses too high?', 'How can I reduce my rent costs?', 'What are typical operating expenses for Boston restaurants?'..."
            showQuickActions={false}
          />
        </div>
      </SectionCard>

      {/* Start-up Expenses Year 1 (Starting Balance Sheet) - Expanded */}
      <SectionCard title="Start-up Expenses Year 1 (Starting Balance Sheet)" color="blue">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Prepared By"
              type="text"
              value={data.startingBalanceSheetMeta?.preparedBy ?? ''}
              onChange={(value) => actions.updateFinancialData('startingBalanceSheetMeta', { ...(data.startingBalanceSheetMeta || {}), preparedBy: value })}
              placeholder="e.g., MKM"
            />
            <FormField
              label="Company Name"
              type="text"
              value={data.startingBalanceSheetMeta?.companyName ?? ''}
              onChange={(value) => actions.updateFinancialData('startingBalanceSheetMeta', { ...data.startingBalanceSheetMeta, companyName: value })}
              placeholder="e.g., Quartermaster"
            />
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Fixed Assets</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Asset</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Amount</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-700">Depreciation (years)</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'realEstateLand', label: 'Real Estate – Land', depKey: 'realEstateLand' },
                    { key: 'realEstateBuildings', label: 'Real Estate – Buildings', depKey: 'realEstateBuildings' },
                    { key: 'leaseholdImprovements', label: 'Leasehold Improvements', depKey: 'leaseholdImprovements' },
                    { key: 'equipment', label: 'Equipment', depKey: 'equipment' },
                    { key: 'furnitureFixtures', label: 'Furniture and Fixtures', depKey: 'furnitureFixtures' },
                    { key: 'vehicles', label: 'Vehicles', depKey: 'vehicles' },
                    { key: 'otherFixedAssets', label: 'Other', depKey: 'other' }
                  ].map(({ key, label, depKey }) => (
                    <tr key={key} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-900">{label}</td>
                      <td className="py-2 px-3 text-right">
                        <input
                          type="number"
                          value={(data.startupCosts || {})[key] ?? ''}
                          onChange={(e) => handleFieldChange('startupCosts', key, parseFloat(e.target.value) || 0)}
                          className="w-28 text-right border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="py-2 px-3 text-center text-gray-600">
                        {(data.fixedAssetsDepreciation || {})[depKey] != null ? `${data.fixedAssetsDepreciation[depKey]} yrs` : 'Not Depreciated'}
                      </td>
                      <td className="py-2 px-3 text-gray-500">—</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="py-2 px-3">Total Fixed Assets</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(
                      ((data.startupCosts || {}).realEstateLand || 0) +
                      ((data.startupCosts || {}).realEstateBuildings || 0) +
                      ((data.startupCosts || {}).leaseholdImprovements || 0) +
                      ((data.startupCosts || {}).equipment || 0) +
                      ((data.startupCosts || {}).furnitureFixtures || 0) +
                      ((data.startupCosts || {}).vehicles || 0) +
                      ((data.startupCosts || {}).otherFixedAssets || 0)
                    )}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Operating Capital</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Item</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'preOpeningSalaries', label: 'Pre-Opening Salaries and Wages' },
                    { key: 'prepaidInsurance', label: 'Prepaid Insurance Premiums' },
                    { key: 'initialInventory', label: 'Inventory' },
                    { key: 'professionalFees', label: 'Legal and Accounting Fees' },
                    { key: 'rentDeposits', label: 'Rent Deposits' },
                    { key: 'utilityDeposits', label: 'Utility Deposits' },
                    { key: 'supplies', label: 'Supplies' },
                    { key: 'initialMarketing', label: 'Advertising and Promotions' },
                    { key: 'depositsLicenses', label: 'Licenses' },
                    { key: 'otherStartup', label: 'Other Initial Start-Up Costs' },
                    { key: 'workingCapitalCashOnHand', label: 'Working Capital (Cash On Hand)' }
                  ].map(({ key, label }) => (
                    <tr key={key} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-900">{label}</td>
                      <td className="py-2 px-3 text-right">
                        <input
                          type="number"
                          value={(data.startupCosts || {})[key] ?? ''}
                          onChange={(e) => handleFieldChange('startupCosts', key, parseFloat(e.target.value) || 0)}
                          className="w-28 text-right border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="py-2 px-3 text-gray-500">—</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="py-2 px-3">Total Operating Capital</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(
                      ((data.startupCosts || {}).preOpeningSalaries || 0) +
                      ((data.startupCosts || {}).prepaidInsurance || 0) +
                      ((data.startupCosts || {}).initialInventory || 0) +
                      ((data.startupCosts || {}).professionalFees || 0) +
                      ((data.startupCosts || {}).rentDeposits || 0) +
                      ((data.startupCosts || {}).utilityDeposits || 0) +
                      ((data.startupCosts || {}).supplies || 0) +
                      ((data.startupCosts || {}).initialMarketing || 0) +
                      ((data.startupCosts || {}).depositsLicenses || 0) +
                      ((data.startupCosts || {}).otherStartup || 0) +
                      ((data.startupCosts || {}).workingCapitalCashOnHand || 0)
                    )}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <span className="text-lg font-semibold text-blue-900">Total Required Funds (Fixed Assets + Operating Capital)</span>
            <span className="text-2xl font-bold text-blue-700">{formatCurrency(calculations.totalStartupCosts)}</span>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Sources of Funding</h4>
            <p className="text-xs text-gray-500 mb-2">Total funding must equal Total Required Funds to be fully funded.</p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Source</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Percentage</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Totals</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Loan Rate</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Term (mo)</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Monthly Payment</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const fs = data.fundingSources || {};
                    const totalFunding = fundingAnalysis.totalFunding || 0;
                    const totalRequired = calculations.totalCapitalNeeded || (calculations.totalStartupCosts + (calculations.cashFlowReserve6Months || 0));
                    const pct = (amt) => totalRequired > 0 ? ((amt / totalRequired) * 100).toFixed(2) + '%' : '0.00%';
                    const rows = [
                      { label: "Owner's Equity", amt: (fs.personalSavings || 0) + (fs.personalAssets || 0) + (fs.homeEquity || 0) + (fs.retirementFunds || 0), rate: null, term: null, payment: null },
                      { label: 'Outside Investors', amt: (fs.angelInvestors || 0) + (fs.ventureCapital || 0) + (fs.privateInvestors || 0), rate: null, term: null, payment: null },
                      { label: 'Owner Financing', amt: (fs.bankLoans || 0) + (fs.familyLoans || 0) + (fs.friendLoans || 0), rate: fs.fundingTerms?.interestRates?.bankLoans ?? 6, term: fs.fundingTerms?.repaymentTerms?.bankLoans ?? 120, payment: fundingAnalysis.debtService?.bankLoans ?? fundingAnalysis.debtService?.familyLoans },
                      { label: 'SBA / BLDC', amt: fs.sbaLoans || 0, rate: fs.fundingTerms?.interestRates?.sbaLoans ?? 6.7, term: fs.fundingTerms?.repaymentTerms?.sbaLoans ?? 84, payment: fundingAnalysis.debtService?.sbaLoans },
                      { label: 'Vehicle Loans', amt: fs.equipmentFinancing || 0, rate: fs.fundingTerms?.interestRates?.equipmentFinancing ?? 6, term: fs.fundingTerms?.repaymentTerms?.equipmentFinancing ?? 48, payment: fundingAnalysis.debtService?.equipmentFinancing },
                      { label: 'Other Bank Debt', amt: (fs.lineOfCredit || 0) + (fs.businessCreditCards || 0), rate: null, term: null, payment: null }
                    ];
                    return rows.map((row, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-900">{row.label}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{pct(row.amt)}</td>
                        <td className="py-2 px-3 text-right font-medium">{formatCurrency(row.amt)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{row.rate != null ? row.rate + '%' : '—'}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{row.term != null ? row.term : '—'}</td>
                        <td className="py-2 px-3 text-right">{row.payment != null ? formatCurrency(row.payment) : '—'}</td>
                        <td className="py-2 px-3 text-gray-500">—</td>
                      </tr>
                    ));
                  })()}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="py-2 px-3">Total Sources of Funding</td>
                    <td className="py-2 px-3 text-right">
                      {((fundingAnalysis.totalFunding || 0) / (calculations.totalCapitalNeeded || 1) * 100).toFixed(2)}%
                    </td>
                    <td className="py-2 px-3 text-right">{formatCurrency(fundingAnalysis.totalFunding)}</td>
                    <td colSpan={2} />
                    <td className="py-2 px-3 text-right">{formatCurrency(fundingAnalysis.totalMonthlyDebtService)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className={`mt-3 p-3 rounded-lg text-sm font-medium ${calculations.capitalGap <= 0 ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
              {calculations.capitalGap <= 0
                ? 'You are fully funded (Balanced).'
                : `Total Funding Needed: ${formatCurrency(calculations.capitalGap)}`}
            </div>
          </div>

          {data.restaurantType?.type === 'existing' && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Existing Businesses ONLY — Calculating Cash on Hand</h4>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <tbody>
                    {['Cash', 'Accounts Receivable', 'Prepaid Expenses', 'Accounts Payable', 'Accrued Expenses'].map((label, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-2 px-3 text-gray-700">{label}</td>
                        <td className="py-2 px-3 text-right text-gray-500">—</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-300 font-semibold">
                      <td className="py-2 px-3">Total Cash on Hand</td>
                      <td className="py-2 px-3 text-right">$ —</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Startup Costs List (line items with example costs) */}
      <SectionCard title="Startup Costs List" description="Line-item list by category. Edit quantities and unit costs; total = Quantity × Unit Cost." color="gray">
        {(() => {
          const lineItems = (data.startupCostLineItems?.items || []).slice();
          const categories = ['Business Set up', 'Equipment and supplies', 'COGS', 'Construction build out'];
          const updateLineItems = (newItems) => {
            actions.updateFinancialData('startupCostLineItems', { items: newItems });
          };
          const updateItem = (id, field, value) => {
            const next = lineItems.map((item) =>
              item.id === id ? { ...item, [field]: field === 'quantity' || field === 'unitCost' ? (parseFloat(value) || 0) : value } : item
            );
            updateLineItems(next);
          };
          const addRow = (category) => {
            const newItem = {
              id: 'sc_' + Date.now(),
              category: category || categories[0],
              description: '',
              unit: '1',
              quantity: 1,
              unitCost: 0
            };
            updateLineItems([...lineItems, newItem]);
          };
          const removeRow = (id) => updateLineItems(lineItems.filter((item) => item.id !== id));
          const mainCategories = ['Business Set up', 'Equipment and supplies', 'COGS', 'Construction build out'];
          const otherRows = lineItems.filter((r) => !mainCategories.includes(r.category));
          const byCategory = mainCategories.map((cat) => ({ category: cat, rows: lineItems.filter((r) => r.category === cat) }));
          if (otherRows.length > 0) byCategory.push({ category: 'Other', rows: otherRows });
          const grandTotal = lineItems.reduce((sum, r) => sum + (r.quantity || 0) * (r.unitCost || 0), 0);
          const formatNum = (n) => (n == null || n === '') ? '' : Number(n);
          return (
            <div className="space-y-6">
              {byCategory.map(({ category, rows }) => {
                const subTotal = rows.reduce((sum, r) => sum + (r.quantity || 0) * (r.unitCost || 0), 0);
                return (
                  <div key={category}>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center justify-between">
                      <span>{category}</span>
                      <span className="text-sm font-normal text-gray-600">{formatCurrency(subTotal)}</span>
                    </h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="w-12 py-2 px-2 font-semibold text-gray-700">No</th>
                            <th className="text-left py-2 px-2 font-semibold text-gray-700">Description of work</th>
                            <th className="text-left py-2 px-2 font-semibold text-gray-700 w-24">Unit</th>
                            <th className="text-right py-2 px-2 font-semibold text-gray-700 w-20">Quantity</th>
                            <th className="text-right py-2 px-2 font-semibold text-gray-700 w-24">Unit Cost</th>
                            <th className="text-right py-2 px-2 font-semibold text-gray-700 w-24">Total</th>
                            <th className="w-10" />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => {
                            const total = (row.quantity || 0) * (row.unitCost || 0);
                            return (
                              <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="py-1 px-2 text-gray-600">{idx + 1}</td>
                                <td className="py-1 px-2">
                                  <input
                                    type="text"
                                    value={row.description || ''}
                                    onChange={(e) => updateItem(row.id, 'description', e.target.value)}
                                    className="w-full min-w-[180px] border border-gray-300 rounded px-2 py-1 text-gray-900"
                                    placeholder="Description"
                                  />
                                </td>
                                <td className="py-1 px-2">
                                  <input
                                    type="text"
                                    value={row.unit || ''}
                                    onChange={(e) => updateItem(row.id, 'unit', e.target.value)}
                                    className="w-full border border-gray-300 rounded px-2 py-1"
                                    placeholder="Unit"
                                  />
                                </td>
                                <td className="py-1 px-2 text-right">
                                  <input
                                    type="number"
                                    value={formatNum(row.quantity)}
                                    onChange={(e) => updateItem(row.id, 'quantity', e.target.value)}
                                    className="w-20 text-right border border-gray-300 rounded px-2 py-1"
                                  />
                                </td>
                                <td className="py-1 px-2 text-right">
                                  <input
                                    type="number"
                                    value={formatNum(row.unitCost)}
                                    onChange={(e) => updateItem(row.id, 'unitCost', e.target.value)}
                                    className="w-24 text-right border border-gray-300 rounded px-2 py-1"
                                  />
                                </td>
                                <td className="py-1 px-2 text-right font-medium">{formatCurrency(total)}</td>
                                <td className="py-1 px-2">
                                  <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700 p-1" aria-label="Remove row">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <button type="button" onClick={() => addRow(category)} className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                      <Plus className="w-4 h-4" /> Add row
                    </button>
                  </div>
                );
              })}
              <div className="pt-4 border-t-2 border-gray-300">
                <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                  <span className="text-lg font-semibold text-gray-900">Total Cost to Opening</span>
                  <span className="text-2xl font-bold text-gray-900">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>
          );
        })()}
      </SectionCard>

      {/* Location-specific startup costs */}
      <SectionCard title={`${data.restaurantDetails?.location || 'Boston'} Restaurant Startup Costs`} color="purple">
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-semibold text-amber-900 mb-2">Pre-opening budget (runway)</h4>
          <p className="text-sm text-amber-800 mb-2">Costs that occur before opening: salaries, rent, training, marketing, deposits.</p>
          <div className="text-xl font-bold text-amber-900">{formatCurrency(preOpeningBudget.total)}</div>
          {preOpeningBudget.total > 0 && (
            <ul className="text-xs text-amber-700 mt-2 space-y-0.5">
              {preOpeningBudget.breakdown.preOpeningSalaries > 0 && <li>Pre-opening salaries: {formatCurrency(preOpeningBudget.breakdown.preOpeningSalaries)}</li>}
              {preOpeningBudget.breakdown.preOpeningRent > 0 && <li>Pre-opening rent: {formatCurrency(preOpeningBudget.breakdown.preOpeningRent)}</li>}
              {preOpeningBudget.breakdown.trainingPreOpen > 0 && <li>Training: {formatCurrency(preOpeningBudget.breakdown.trainingPreOpen)}</li>}
              {preOpeningBudget.breakdown.professionalFees > 0 && <li>Professional fees: {formatCurrency(preOpeningBudget.breakdown.professionalFees)}</li>}
              {preOpeningBudget.breakdown.initialMarketing > 0 && <li>Initial marketing: {formatCurrency(preOpeningBudget.breakdown.initialMarketing)}</li>}
              {preOpeningBudget.breakdown.depositsLicenses > 0 && <li>Deposits & licenses: {formatCurrency(preOpeningBudget.breakdown.depositsLicenses)}</li>}
            </ul>
          )}
        </div>
        <div className="mb-4 p-4 bg-purple-50 rounded-lg">
          <h4 className="font-semibold text-purple-800 mb-2">{data.restaurantDetails?.location || 'Boston'}-Specific Requirements:</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• {data.restaurantDetails?.location || 'Boston'} business license & food service permit</li>
            <li>• MA alcoholic beverage license (if applicable)</li>
            <li>• Fire department approval & sprinkler system</li>
            <li>• Board of Health inspection & certification</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Leasehold Improvements"
            type="number"
            value={data.startupCosts.leaseholdImprovements}
            onChange={(value) => handleFieldChange('startupCosts', 'leaseholdImprovements', value)}
            placeholder="200000"
          />
          <FormField
            label="Kitchen Equipment"
            type="number"
            value={data.startupCosts.kitchenEquipment}
            onChange={(value) => handleFieldChange('startupCosts', 'kitchenEquipment', value)}
            placeholder="125000"
          />
          <FormField
            label="Furniture & Fixtures"
            type="number"
            value={data.startupCosts.furnitureFixtures}
            onChange={(value) => handleFieldChange('startupCosts', 'furnitureFixtures', value)}
            placeholder="40000"
          />
          <FormField
            label="Initial Inventory"
            type="number"
            value={data.startupCosts.initialInventory}
            onChange={(value) => handleFieldChange('startupCosts', 'initialInventory', value)}
            placeholder="15000"
          />
          <FormField
            label={`${data.restaurantDetails?.location || 'Boston'} Permits & Licenses`}
            type="number"
            value={data.startupCosts.depositsLicenses}
            onChange={(value) => handleFieldChange('startupCosts', 'depositsLicenses', value)}
            placeholder="25000"
          />
          <FormField
            label="Pre-Opening Marketing"
            type="number"
            value={data.startupCosts.initialMarketing}
            onChange={(value) => handleFieldChange('startupCosts', 'initialMarketing', value)}
            placeholder="15000"
          />
          <FormField
            label="Pre-Opening Salaries"
            type="number"
            value={data.startupCosts.preOpeningSalaries}
            onChange={(value) => handleFieldChange('startupCosts', 'preOpeningSalaries', value)}
            placeholder="30000"
          />
          <FormField
            label="Contingency Reserve"
            type="number"
            value={data.startupCosts.contingency}
            onChange={(value) => handleFieldChange('startupCosts', 'contingency', value)}
            placeholder="25000"
          />
          <FormField
            label="Signage"
            type="number"
            value={data.startupCosts.signage}
            onChange={(value) => handleFieldChange('startupCosts', 'signage', value)}
            placeholder="5000"
          />
          <FormField
            label="POS Hardware & Setup"
            type="number"
            value={data.startupCosts.posHardware}
            onChange={(value) => handleFieldChange('startupCosts', 'posHardware', value)}
            placeholder="8000"
          />
          <FormField
            label="Smallwares (Opening)"
            type="number"
            value={data.startupCosts.smallwaresStartup}
            onChange={(value) => handleFieldChange('startupCosts', 'smallwaresStartup', value)}
            placeholder="10000"
          />
          <FormField
            label="Opening Beverage Inventory"
            type="number"
            value={data.startupCosts.openingBeverageInventory}
            onChange={(value) => handleFieldChange('startupCosts', 'openingBeverageInventory', value)}
            placeholder="8000"
          />
          <FormField
            label="Pre-Opening Training"
            type="number"
            value={data.startupCosts.trainingPreOpen}
            onChange={(value) => handleFieldChange('startupCosts', 'trainingPreOpen', value)}
            placeholder="3000"
          />
          <FormField
            label="Security System Install"
            type="number"
            value={data.startupCosts.securityInstall}
            onChange={(value) => handleFieldChange('startupCosts', 'securityInstall', value)}
            placeholder="4000"
          />
          <FormField
            label="Hood & Ventilation"
            type="number"
            value={data.startupCosts.hoodVentilation}
            onChange={(value) => handleFieldChange('startupCosts', 'hoodVentilation', value)}
            placeholder="15000"
          />
          <FormField
            label="Plumbing & Electrical"
            type="number"
            value={data.startupCosts.plumbingElectrical}
            onChange={(value) => handleFieldChange('startupCosts', 'plumbingElectrical', value)}
            placeholder="20000"
          />
          <FormField
            label="Legal / Architect / Design"
            type="number"
            value={data.startupCosts.professionalFees}
            onChange={(value) => handleFieldChange('startupCosts', 'professionalFees', value)}
            placeholder="15000"
          />
          <FormField
            label="Pre-Opening Rent"
            type="number"
            value={data.startupCosts.preOpeningRent}
            onChange={(value) => handleFieldChange('startupCosts', 'preOpeningRent', value)}
            placeholder="0"
          />
          <FormField
            label="Other Startup"
            type="number"
            value={data.startupCosts.otherStartup}
            onChange={(value) => handleFieldChange('startupCosts', 'otherStartup', value)}
            placeholder="0"
          />
        </div>

        <div className="mt-6 space-y-3">
          <div className="p-4 bg-purple-100 rounded-lg flex items-center justify-between">
            <span className="text-lg font-semibold">Total Startup Investment:</span>
            <span className="text-2xl font-bold text-purple-600">
              {formatCurrency(calculations.totalStartupCosts)}
            </span>
          </div>
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
            <div>
              <span className="text-lg font-semibold text-gray-900">Cash flow needs (6-month reserve):</span>
              <p className="text-sm text-gray-600 mt-0.5">Reserve of operating expenses for first 6 months</p>
            </div>
            <span className="text-xl font-bold text-indigo-700">
              {formatCurrency(calculations.cashFlowReserve6Months)}
            </span>
          </div>
          <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-900">Total capital needed (startup + 6‑mo reserve):</span>
            <span className="text-2xl font-bold text-gray-800">
              {formatCurrency(calculations.totalCapitalNeeded)}
            </span>
          </div>
        </div>
        
        {/* AI Assistant for Startup Costs */}
        <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center space-x-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">AI Startup Cost Advisor</h4>
          </div>
          <AIAssistant
            context={{
              startupCosts: data.startupCosts,
              totalStartupCosts: calculations.totalStartupCosts,
              restaurantType: restaurantType,
              cashFlowReserve6Months: calculations.cashFlowReserve6Months,
              workingCapitalNeeded: calculations.workingCapitalNeeded,
              totalCapitalNeeded: calculations.totalCapitalNeeded,
              capitalGap: calculations.capitalGap,
              bostonBenchmarks: bostonBenchmarks
            }}
            section="startupCosts"
            placeholder="Ask: 'Are my startup costs realistic?', 'What startup costs am I missing?', 'How much should I budget for Boston permits?'..."
            showQuickActions={false}
          />
        </div>
      </SectionCard>

      {/* P&L line items & vendor links – like an actual P&L, parsed monthly in Monthly Statement */}
      <SectionCard title="P&L line items & vendor links" color="blue">
        <p className="text-sm text-gray-600 mb-4">
          Link expenses to vendors. Amounts are parsed monthly in the Monthly Financial Statement. Assign a vendor to track who you pay for each line.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Operating expenses (annual → monthly)</h4>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Category</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Annual</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {OPERATING_EXPENSE_KEYS.map(key => (
                    <tr key={key} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-3 text-gray-900">{OPERATING_EXPENSE_LABELS[key] || key}</td>
                      <td className="py-1.5 px-3 text-right text-gray-700">
                        {formatCurrency((data.operatingExpenses || {})[key] || 0)}
                      </td>
                      <td className="py-1.5 px-3">
                        <select
                          value={expenseVendors.operating[key] || ''}
                          onChange={(e) => setExpenseVendor('operating', key, e.target.value || null)}
                          className="w-full max-w-[180px] text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">— None —</option>
                          {(state.vendors || []).map(v => (
                            <option key={v.id} value={String(v.id)}>{v.company || v.name || `Vendor ${v.id}`}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Startup costs (one-time, amortized in P&L)</h4>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Category</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Amount</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {STARTUP_COST_KEYS.map(key => (
                    <tr key={key} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-3 text-gray-900">{STARTUP_COST_LABELS[key] || key}</td>
                      <td className="py-1.5 px-3 text-right text-gray-700">
                        {formatCurrency((data.startupCosts || {})[key] || 0)}
                      </td>
                      <td className="py-1.5 px-3">
                        <select
                          value={expenseVendors.startup[key] || ''}
                          onChange={(e) => setExpenseVendor('startup', key, e.target.value || null)}
                          className="w-full max-w-[180px] text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">— None —</option>
                          {(state.vendors || []).map(v => (
                            <option key={v.id} value={String(v.id)}>{v.company || v.name || `Vendor ${v.id}`}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Financial Health Dashboard */}
      <SectionCard title="Financial Health Dashboard" color="gray">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${calculations.grossMargin >= 65 ? 'bg-green-100' : calculations.grossMargin >= 60 ? 'bg-yellow-100' : 'bg-red-100'}`}>
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="font-semibold">Gross Margin</span>
            </div>
            <div className="text-2xl font-bold">{calculations.grossMargin.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Target: 65%+</div>
          </div>

          <div className={`p-4 rounded-lg ${calculations.netMargin >= 10 ? 'bg-green-100' : calculations.netMargin >= 5 ? 'bg-yellow-100' : 'bg-red-100'}`}>
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="font-semibold">Net Margin</span>
            </div>
            <div className="text-2xl font-bold">{calculations.netMargin.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Target: 10%+</div>
          </div>

          <div className={`p-4 rounded-lg ${calculations.fundingGap <= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
            <div className="flex items-center space-x-2 mb-2">
              <Calculator className="w-5 h-5" />
              <span className="font-semibold">Funding Gap</span>
            </div>
            <div className="text-2xl font-bold">{formatCurrency(Math.abs(calculations.fundingGap))}</div>
            <div className="text-sm text-gray-600">
              {calculations.fundingGap <= 0 ? 'Fully Funded' : 'Additional Needed'}
            </div>
          </div>

          <div className="p-4 bg-blue-100 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">Break-Even</span>
            </div>
            <div className="text-2xl font-bold">{formatCurrency(calculations.breakEvenRevenue)}</div>
            <div className="text-sm text-gray-600">Monthly Revenue Needed</div>
          </div>
        </div>
      </SectionCard>

      {/* AI Financial Advisor - Enhanced */}
      <SectionCard title="AI Financial Advisor" color="indigo" icon={Sparkles}>
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
            <div className="flex items-center space-x-2 mb-3">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <h4 className="text-lg font-semibold text-gray-900">AI-Powered Financial Analysis</h4>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Get intelligent insights, recommendations, and analysis of your financial projections. AI can help identify risks, 
              optimize costs, suggest improvements, and provide industry benchmarks.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Risk analysis & mitigation</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Cost optimization suggestions</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Revenue growth strategies</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Industry benchmark comparisons</span>
              </div>
            </div>
          </div>
          
          <AIAssistant
            context={{
              financialData: data,
              projections: dailySalesProjections,
              calculations: calculations,
              restaurantOperations: data.restaurantOperations,
              operatingExpenses: data.operatingExpenses,
              startupCosts: data.startupCosts,
              fundingSources: data.fundingSources,
              bostonBenchmarks: bostonBenchmarks,
              restaurantType: restaurantType,
              totalRevenue: calculations.totalRevenue,
              grossMargin: calculations.grossMargin,
              netMargin: calculations.netMargin,
              breakEvenRevenue: calculations.breakEvenRevenue,
              monthlyBurnRate: calculations.monthlyBurnRate,
              runwayMonths: calculations.runwayMonths,
              capitalGap: calculations.capitalGap
            }}
            section="financialProjections"
            placeholder="Ask: 'What are the biggest risks in my financial plan?', 'How can I improve my profit margins?', 'Analyze my cash flow projections', or 'Compare my costs to industry benchmarks'..."
            onGenerate={(recommendations) => {
              if (recommendations && typeof recommendations === 'string') {
                actions.showMessage(
                  'AI Recommendations',
                  recommendations.substring(0, 500) + (recommendations.length > 500 ? '...' : ''),
                  'info'
                );
              }
            }}
            showQuickActions={true}
          />
        </div>
      </SectionCard>

      {/* P&L Statement Importer */}
      <SectionCard title="Import P&L Statement" color="green">
        <PLImporter />
        <ExcelFinancialImporter />
      </SectionCard>

      <SectionCard title="Generate Investor Documents" color="purple">
        <DocumentGenerator
          onDocumentGenerated={(type) => {
            const next = [...(state.documentVersions || []), { id: `doc_${Date.now()}`, type, generatedAt: new Date() }];
            actions.updateDocumentVersions(next);
          }}
        />
      </SectionCard>

      {/* Financial Insights & Export */}
      <SectionCard title="Financial Insights & Export" color="emerald">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Financial Insights */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-emerald-600" />
              Key Financial Insights
            </h3>
            
            <div className="space-y-4">
              {/* Profitability Analysis */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Profitability Analysis</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gross Profit:</span>
                    <span className="font-medium">{formatCurrency(calculations.grossProfit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Income:</span>
                    <span className={`font-medium ${calculations.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(calculations.netIncome)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Break-even Revenue:</span>
                    <span className="font-medium">{formatCurrency(calculations.breakEvenRevenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue vs Break-even:</span>
                    <span className={`font-medium ${calculations.totalRevenue >= calculations.breakEvenRevenue ? 'text-green-600' : 'text-red-600'}`}>
                      {calculations.totalRevenue >= calculations.breakEvenRevenue ? 'Above' : 'Below'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cash Flow Analysis */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Cash Flow Analysis</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Burn Rate:</span>
                    <span className="font-medium">{formatCurrency(calculations.monthlyBurnRate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cash Runway:</span>
                    <span className="font-medium">{calculations.runwayMonths.toFixed(1)} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time to Break-even:</span>
                    <span className="font-medium">{Math.ceil(calculations.monthsToBreakEven)} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Funding Status:</span>
                    <span className={`font-medium ${calculations.fundingGap <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {calculations.fundingGap <= 0 ? 'Fully Funded' : 'Funding Gap'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Boston Market Comparison */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{data.restaurantDetails?.location || 'Boston'} Market Comparison</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Food Cost %:</span>
                    <span className={`font-medium ${getHealthStatus(data.cogs.foodCogsPercent, bostonBenchmarks.avgFoodCostPercent, false) === 'excellent' ? 'text-green-600' : 'text-red-600'}`}>
                      {(data.cogs.foodCogsPercent * 100).toFixed(1)}% vs {bostonBenchmarks.avgFoodCostPercent * 100}% avg
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Labor Cost %:</span>
                    <span className={`font-medium ${getHealthStatus((data.operatingExpenses.salaryFullTime + data.operatingExpenses.salaryPartTime) / calculations.totalRevenue, bostonBenchmarks.avgLaborPercent, false) === 'excellent' ? 'text-green-600' : 'text-red-600'}`}>
                      {((data.operatingExpenses.salaryFullTime + data.operatingExpenses.salaryPartTime) / calculations.totalRevenue * 100).toFixed(1)}% vs {bostonBenchmarks.avgLaborPercent * 100}% avg
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Startup Cost per Seat:</span>
                    <span className="font-medium">
                      {formatCurrency(calculations.totalStartupCosts / (data.restaurantDetails?.seats || 50))} vs {formatCurrency(bostonBenchmarks.avgStartupCostPerSeat)} avg
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Export & Actions */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Download className="h-5 w-5 mr-2 text-emerald-600" />
              Export & Financial Reports
            </h3>
            
            <div className="space-y-4">
              {/* Export Options */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Export Financial Data</h4>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      const csvContent = [
                        ['Category', 'Value', 'Notes'],
                        ['Total Revenue', formatCurrency(calculations.totalRevenue), 'Annual projected revenue'],
                        ['Gross Profit', formatCurrency(calculations.grossProfit), `Gross margin: ${calculations.grossMargin.toFixed(1)}%`],
                        ['Net Income', formatCurrency(calculations.netIncome), `Net margin: ${calculations.netMargin.toFixed(1)}%`],
                        ['Total Startup Costs', formatCurrency(calculations.totalStartupCosts), 'One-time startup investment'],
                        ['Total Funding', formatCurrency(calculations.totalFunding), 'Available capital'],
                        ['Funding Gap', formatCurrency(calculations.fundingGap), calculations.fundingGap > 0 ? 'Additional funding needed' : 'Fully funded'],
                        ['Break-even Revenue', formatCurrency(calculations.breakEvenRevenue), 'Monthly revenue needed to break even'],
                        ['Monthly Burn Rate', formatCurrency(calculations.monthlyBurnRate), 'Monthly cash outflow'],
                        ['Cash Runway', `${calculations.runwayMonths.toFixed(1)} months`, 'How long funding will last'],
                        ['Time to Break-even', `${Math.ceil(calculations.monthsToBreakEven)} months`, 'When restaurant becomes profitable']
                      ].map(row => row.join(',')).join('\n');

                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'restaurant-financial-summary.csv';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Financial Summary (CSV)
                  </button>
                  
                  <button
                    onClick={() => {
                      const report = `
RESTAURANT FINANCIAL PROJECTION REPORT
Generated: ${new Date().toLocaleDateString()}

FINANCIAL OVERVIEW
==================
Total Annual Revenue: ${formatCurrency(calculations.totalRevenue)}
Gross Profit: ${formatCurrency(calculations.grossProfit)} (${calculations.grossMargin.toFixed(1)}%)
Net Income: ${formatCurrency(calculations.netIncome)} (${calculations.netMargin.toFixed(1)}%)

STARTUP COSTS
=============
Total Startup Investment: ${formatCurrency(calculations.totalStartupCosts)}
Funding Available: ${formatCurrency(calculations.totalFunding)}
Funding Gap: ${formatCurrency(calculations.fundingGap)}

CASH FLOW ANALYSIS
==================
Monthly Burn Rate: ${formatCurrency(calculations.monthlyBurnRate)}
Cash Runway: ${calculations.runwayMonths.toFixed(1)} months
Break-even Revenue: ${formatCurrency(calculations.breakEvenRevenue)}
Time to Break-even: ${Math.ceil(calculations.monthsToBreakEven)} months

BOSTON MARKET BENCHMARKS
========================
Target Food Cost: ${(bostonBenchmarks.avgFoodCostPercent * 100).toFixed(0)}%
Target Labor Cost: ${(bostonBenchmarks.avgLaborPercent * 100).toFixed(0)}%
Average Startup Cost per Seat: ${formatCurrency(bostonBenchmarks.avgStartupCostPerSeat)}

RECOMMENDATIONS
===============
${calculations.fundingGap > 0 ? `• Secure additional funding of ${formatCurrency(calculations.fundingGap)}` : '• Funding requirements met'}
• Target monthly revenue: ${formatCurrency(calculations.totalRevenue / 12)}
• Monitor cash flow closely during first ${Math.ceil(calculations.monthsToBreakEven)} months
• Maintain food costs below ${(bostonBenchmarks.avgFoodCostPercent * 100).toFixed(0)}%
• Keep labor costs under ${(bostonBenchmarks.avgLaborPercent * 100).toFixed(0)}% of revenue
                      `;

                      const blob = new Blob([report], { type: 'text/plain' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'restaurant-financial-report.txt';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Detailed Report (TXT)
                  </button>
                </div>
              </div>

              {/* Financial Health Score */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Financial Health Score</h4>
                <div className="text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">
                    {(() => {
                      let score = 100;
                      if (calculations.fundingGap > 0) score -= 20;
                      if (calculations.grossMargin < 60) score -= 15;
                      if (calculations.netMargin < 5) score -= 15;
                      if (calculations.runwayMonths < 6) score -= 20;
                      if (data.cogs.foodCogsPercent > 0.35) score -= 10;
                      return Math.max(0, score);
                    })()}/100
                  </div>
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const score = (() => {
                        let s = 100;
                        if (calculations.fundingGap > 0) s -= 20;
                        if (calculations.grossMargin < 60) s -= 15;
                        if (calculations.netMargin < 5) s -= 15;
                        if (calculations.runwayMonths < 6) s -= 20;
                        if (data.cogs.foodCogsPercent > 0.35) s -= 10;
                        return Math.max(0, s);
                      })();
                      
                      if (score >= 80) return 'Excellent - Strong financial foundation';
                      if (score >= 60) return 'Good - Some areas need attention';
                      if (score >= 40) return 'Fair - Several improvements needed';
                      return 'Needs Work - Significant financial challenges';
                    })()}
                  </div>
                </div>
              </div>

              {/* Action Items */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Recommended Actions</h4>
                <div className="space-y-2 text-sm">
                  {calculations.fundingGap > 0 && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-yellow-700">Secure additional funding of {formatCurrency(calculations.fundingGap)}</span>
                    </div>
                  )}
                  {calculations.grossMargin < 60 && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-yellow-700">Improve gross margin - target 60%+</span>
                    </div>
                  )}
                  {calculations.runwayMonths < 6 && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-red-700">Increase cash runway - target 6+ months</span>
                    </div>
                  )}
                  {data.cogs.foodCogsPercent > 0.35 && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-yellow-700">Reduce food costs - target 28-32%</span>
                    </div>
                  )}
                  {calculations.netMargin < 5 && (
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-yellow-700">Improve net margin - target 5%+</span>
                    </div>
                  )}
                  {calculations.fundingGap <= 0 && calculations.grossMargin >= 60 && calculations.runwayMonths >= 6 && (
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-green-700">Strong financial foundation - ready to proceed</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Monthly Statement - For Open Restaurants */}
      <SectionCard id="monthly-statement" title="Monthly Financial Statement" icon={FileText} color="purple">
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Track actuals vs. plan:</strong> Choose a month and use <strong>Opening P&L (vs plan)</strong> to compare your real numbers to the opening projections you entered above. 
            Click any &quot;Actual&quot; value to edit. Use <strong>Simple (calculated)</strong> to compare to a derived forecast instead.
          </p>
        </div>
        <MonthlyStatement />
      </SectionCard>

      {/* AI Assistant */}
      <AIAssistant
        context={{
          financialData: data,
          calculations: calculations,
          dailySalesProjections: dailySalesProjections,
          laborProjections: laborProjections,
          fundingAnalysis: fundingAnalysis,
          financialAnalysis: financialAnalysis,
          breakEvenRevenue: calculations.breakEvenRevenue,
          monthlyBurnRate: calculations.monthlyBurnRate,
          runwayMonths: calculations.runwayMonths,
          capitalGap: calculations.capitalGap
        }}
        section="financialProjections"
        placeholder="Ask: 'What are the biggest risks in my financial plan?', 'How can I improve my profit margins?', 'Analyze my cash flow projections', or 'Compare my costs to industry benchmarks'..."
      />
    </div>
    </FeatureGate>
  );
};

export default FinancialProjections; 